import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Activity, Droplets, AlertTriangle, Home, Download, FileText, Filter, DollarSign, Eye, EyeOff, Crown, Scale, TrendingUp, TrendingDown, Target, ShoppingBag, BarChart3, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageTourButton from '../components/PageTourButton';
import { ApiaryQueenComparison, buildApiaryQueenStats } from '../components/queen/ApiaryQueenComparison';
import { ApiaryYieldComparison } from '../components/harvest/ApiaryYieldComparison';
import SplitResultsCard from '../components/hive/SplitResultsCard';
import { toUnit, displayUnit, kgTo, harvestYard } from '../utils/harvestStats';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTreatmentName = (name) => {
  if (!name) return '';
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const COLORS = {
  healthy: '#65A30D',
  warning: '#EAB308',
  critical: '#DC2626',
  unknown: '#78716C'
};

const Analytics = () => {
  const navigate = useNavigate();
  const [hives, setHives] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yardStats, setYardStats] = useState({});
  const [healthData, setHealthData] = useState([]);
  const [nectarData, setNectarData] = useState([]);
  const [geocodedYards, setGeocodedYards] = useState({});
  const [mapCenter, setMapCenter] = useState([-38.0, 145.0]); // Default to Victoria, Australia
  const [mapReady, setMapReady] = useState(false);
  const [hivesByHealth, setHivesByHealth] = useState({ healthy: [], warning: [], critical: [], unknown: [] });
  const [fedHives, setFedHives] = useState([]);
  const [showFedHivesModal, setShowFedHivesModal] = useState(false);
  const [showDueInspectionModal, setShowDueInspectionModal] = useState(false);
  const [showQueenStatusModal, setShowQueenStatusModal] = useState(null); // 'spotted', 'noQueen', 'cells', 'droneLayer'
  const [showDiseasePestModal, setShowDiseasePestModal] = useState(null); // 'diseases' or 'pests'
  const [showMiteLoadModal, setShowMiteLoadModal] = useState(null); // 'low', 'moderate', 'high'
  const [equipmentSummary, setEquipmentSummary] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [yards, setYards] = useState([]);
  const [honeyHarvestData, setHoneyHarvestData] = useState({});
  const [selectedYard, setSelectedYard] = useState('all'); // NEW: Yard filter state
  const [costSummary, setCostSummary] = useState(null); // Cost summary data
  const [showCosts, setShowCosts] = useState(false); // Toggle for cost column visibility
  const [queenRearingRaw, setQueenRearingRaw] = useState(null); // Queen Rearing analytics
  const [harvestSummary, setHarvestSummary] = useState(null); // Harvest summary from /harvests/summary
  const [harvests, setHarvests] = useState([]); // Full harvest list
  const [yieldSeason, setYieldSeason] = useState('');
  const [miteCountRecords, setMiteCountRecords] = useState([]); // Mite count records
  const [weightAnalytics, setWeightAnalytics] = useState(null); // Weight analytics data
  const [treatmentData, setTreatmentData] = useState({}); // Treatment summary by type
  const [treatmentPlans, setTreatmentPlans] = useState([]); // Treatment plan history
  const [toolsConfig, setToolsConfig] = useState(null); // Tools configuration for dynamic categories
  const [componentsConfig, setComponentsConfig] = useState(null); // Components configuration for dynamic categories
  const [harvestConfig, setHarvestConfig] = useState(null); // Harvest configuration including equipment
  const [salesSummary, setSalesSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [swarmTrapSummary, setSwarmTrapSummary] = useState(null); // Swarm trap analytics

  useEffect(() => {
    fetchData();
  }, []);

  // Geocode all yards using backend API
  const geocodeAllYards = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/geocode-yards`, {}, { headers });
      const geocoded = response.data;
      
      setGeocodedYards(geocoded);
      
      // Calculate center of all valid coordinates
      const validCoords = Object.values(geocoded);
      if (validCoords.length > 0) {
        const avgLat = validCoords.reduce((sum, c) => sum + c.lat, 0) / validCoords.length;
        const avgLng = validCoords.reduce((sum, c) => sum + c.lng, 0) / validCoords.length;
        setMapCenter([avgLat, avgLng]);
      }
      
      setMapReady(true);
    } catch (error) {
      setMapReady(true); // Show map anyway with fallback
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token && localStorage.getItem('demoMode') === 'true') {
        setLoading(false);
        return;
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [hivesRes, inspectionsRes, yardsRes, equipmentRes, companyRes, costRes, batchesRes, breedersRes, nucsRes, harvestSummaryRes, miteCountsRes, harvestsRes, toolsConfigRes, componentsConfigRes, toolCategoriesRes, componentCategoriesRes, harvestConfigRes, treatmentPlansRes, salesSummaryRes, productsRes] = await Promise.all([
        axios.get(`${API}/hives`, { headers }),
        axios.get(`${API}/inspections`, { headers }),
        axios.get(`${API}/yards`, { headers }),
        axios.get(`${API}/equipment-summary`, { headers }),
        axios.get(`${API}/company-profile`, { headers }),
        axios.get(`${API}/cost-summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/queen-rearing/batches`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/queen-rearing/breeders`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/queen-rearing/mating-nucs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/harvests/summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/mite-counts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/harvests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/config/admin_tools`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/config/admin_components`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/config/custom-categories/tools`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/config/custom-categories/components`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/harvest-config`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/treatment-plans`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/sales/summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/products`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      const hivesData = hivesRes.data;
      const inspectionsData = inspectionsRes.data;
      const yardsData = yardsRes.data;
      
      setHives(hivesData);
      setInspections(inspectionsData);
      setYards(yardsData);
      setEquipmentSummary(equipmentRes.data);
      setCompanyProfile(companyRes.data);
      setCostSummary(costRes.data);
      // Store tools config as object with items and custom_categories
      setToolsConfig({
        items: toolsConfigRes.data || [],
        custom_categories: toolCategoriesRes.data || []
      });
      setComponentsConfig({
        items: componentsConfigRes.data || [],
        custom_categories: componentCategoriesRes.data || []
      });
      setHarvestConfig(harvestConfigRes.data);
      
      // Process Queen Rearing data
      const batches = batchesRes.data || [];
      const breeders = breedersRes.data || [];
      const nucs = nucsRes.data || [];
      
      setQueenRearingRaw({ batches, breeders, nucs });
      
      // Set harvest summary (from /harvests/summary endpoint - matches Dashboard)
      setHarvestSummary(harvestSummaryRes.data);
      setHarvests(harvestsRes.data || []);
      
      // Fetch sales summary
      // Process sales and products (fetched in Promise.all above)
      setSalesSummary(salesSummaryRes.data);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : productsRes.data?.products || []);
      
      // Process mite count records
      const miteCounts = miteCountsRes.data || [];
      setMiteCountRecords(miteCounts);
      
      // Store treatment plans
      const tpData = treatmentPlansRes.data;
      setTreatmentPlans(Array.isArray(tpData) ? tpData : (tpData?.plans || []));
      
      // Fetch weight analytics
      try {
        const weightRes = await axios.get(`${API}/analytics/weight-summary`, { headers });
        setWeightAnalytics(weightRes.data);
      } catch (err) {
      }
      
      // Fetch swarm trap summary
      try {
        const swarmRes = await axios.get(`${API}/swarm-traps/summary`, { headers });
        setSwarmTrapSummary(swarmRes.data);
      } catch (err) {
      }
      
      // Calculate treatment summary from inspections
      const treatments = {};
      inspectionsData.forEach(insp => {
        if (insp.mite_treatment && insp.mite_treatment.length > 0) {
          insp.mite_treatment.forEach(treatment => {
            if (!treatments[treatment]) {
              treatments[treatment] = { count: 0, hives: new Set() };
            }
            treatments[treatment].count++;
            treatments[treatment].hives.add(insp.hive_id);
          });
        }
      });
      // Convert Sets to arrays for state
      Object.keys(treatments).forEach(t => {
        treatments[t].hives = [...treatments[t].hives];
      });
      setTreatmentData(treatments);
      
      calculateStats(hivesData, inspectionsData, yardsData);
      
      // Geocode yard addresses via backend API
      geocodeAllYards();
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (hivesData, inspectionsData, yardsData = []) => {
    const yards = {};
    const healthCounts = { healthy: 0, warning: 0, critical: 0, unknown: 0 };
    const healthHives = { healthy: [], warning: [], critical: [], unknown: [] };
    const honeyByYard = {};
    let hivesBeingFedCount = 0;
    const fedHivesList = [];

    hivesData.forEach(hive => {
      const yard = hive.yard || 'No Apiary';
      if (!yards[yard]) {
        yards[yard] = { count: 0, hives: [] };
      }
      yards[yard].count++;
      yards[yard].hives.push(hive);

      const recentInspections = inspectionsData.filter(i => i.hive_id === hive.id);
      const latestInspection = recentInspections[0];

      if (latestInspection) {
        const daysSince = Math.floor((new Date() - new Date(latestInspection.date)) / (1000 * 60 * 60 * 24));
        
        const hasDisease = latestInspection.diseases && latestInspection.diseases.length > 0;
        const hasPests = latestInspection.pests && latestInspection.pests.length > 0;
        const lowResources = latestInspection.nectar_level === 'L' || 
                            latestInspection.pollen_level === 'L' || 
                            latestInspection.honey_brood_level === 'L';
        const honeyFrames = latestInspection.honey_frames?.length || 0;
        
        if (hasDisease || hasPests || daysSince > 21) {
          healthCounts.critical++;
          healthHives.critical.push({ ...hive, reason: hasDisease ? 'Disease' : hasPests ? 'Pests' : 'Overdue' });
        } else if (daysSince > 14 || latestInspection.population === 'Low' || lowResources || honeyFrames < 3) {
          healthCounts.warning++;
          healthHives.warning.push({ ...hive, reason: daysSince > 14 ? 'Needs inspection' : lowResources ? 'Low resources' : 'Low population' });
        } else {
          healthCounts.healthy++;
          healthHives.healthy.push(hive);
        }

        // Count feeding
        if (latestInspection.feeding === 'Fed') {
          hivesBeingFedCount++;
          fedHivesList.push({ ...hive, feedType: latestInspection.feed_type?.join(', ') || 'Unknown' });
        }

        // Honey frames by yard - use honey_frames_count (number field) or fall back to honey_frames array length
        const honeyCount = latestInspection.honey_frames_count 
          ? parseInt(latestInspection.honey_frames_count) 
          : (latestInspection.honey_frames?.length || 0);
        
        // Always track the yard, even with 0 honey frames
        if (!honeyByYard[yard]) {
          honeyByYard[yard] = { count: 0, total: 0 };
        }
        honeyByYard[yard].count++;
        honeyByYard[yard].total += honeyCount;
      } else {
        healthCounts.unknown++;
        healthHives.unknown.push({ ...hive, reason: 'No inspection' });
      }
    });

    setYardStats(yards);
    setHivesByHealth(healthHives);
    setFedHives(fedHivesList);
    
    setHealthData([
      { name: 'Healthy', value: healthCounts.healthy, color: COLORS.healthy, hives: healthHives.healthy },
      { name: 'Warning', value: healthCounts.warning, color: COLORS.warning, hives: healthHives.warning },
      { name: 'Critical', value: healthCounts.critical, color: COLORS.critical, hives: healthHives.critical },
      { name: 'Unknown', value: healthCounts.unknown, color: COLORS.unknown, hives: healthHives.unknown }
    ].filter(d => d.value > 0));

    // Build honey frames data per hive, grouped by yard
    const honeyByHive = [];
    hivesData.forEach(hive => {
      const hiveInspections = inspectionsData.filter(i => i.hive_id === hive.id);
      if (hiveInspections.length > 0) {
        // Get latest inspection
        const latestInsp = hiveInspections.reduce((latest, curr) => 
          new Date(curr.date) > new Date(latest.date) ? curr : latest
        );
        const honeyCount = latestInsp.honey_frames_count 
          ? parseInt(latestInsp.honey_frames_count) 
          : (latestInsp.honey_frames?.length || 0);
        
        honeyByHive.push({
          hive: `Hive ${hive.hive_number}`,
          yard: hive.yard || 'Unassigned',
          'Honey Frames': honeyCount
        });
      }
    });
    
    // Sort by yard then by hive number
    honeyByHive.sort((a, b) => {
      if (a.yard !== b.yard) return a.yard.localeCompare(b.yard);
      return a.hive.localeCompare(b.hive);
    });
    
    setNectarData(honeyByHive);
    
    // Calculate Honey Harvest data from removed Full Supers and Capped Honey Frames
    const harvestByYard = {};
    
    // Pre-populate all yards
    yardsData.forEach(yard => {
      harvestByYard[yard.name] = { fullSupers: 0, cappedFrames: 0 };
    });
    
    inspectionsData.forEach(insp => {
      const hive = hivesData.find(h => h.id === insp.hive_id);
      const yard = hive?.yard || 'Unassigned';
      
      if (!harvestByYard[yard]) {
        harvestByYard[yard] = { fullSupers: 0, cappedFrames: 0 };
      }
      
      // Check if Full Super was removed (harvested) - check both new and legacy action names
      if (insp.actions?.includes('Removed Full Super')) {
        const qty = parseInt(insp.removed_full_super_qty) || 1; // Default to 1 if action exists but no qty
        harvestByYard[yard].fullSupers += qty;
      }
      // Also check legacy "Removed Super" as potential harvest
      if (insp.actions?.includes('Removed Super') && insp.removed_super_qty) {
        harvestByYard[yard].fullSupers += parseInt(insp.removed_super_qty) || 0;
      }
      
      // Check if Capped Honey Frames were removed (harvested)
      if (insp.actions?.includes('Removed Capped Honey Frames')) {
        const qty = parseInt(insp.removed_capped_honey_frames_qty) || 1; // Default to 1 if action exists but no qty
        harvestByYard[yard].cappedFrames += qty;
      }
    });
    
    setHoneyHarvestData(harvestByYard);
    setHivesBeingFed(hivesBeingFedCount);
  };

  const [hivesBeingFed, setHivesBeingFed] = useState(0);
  const [exporting, setExporting] = useState(false);
  const analyticsRef = useRef(null);
  
  // Handle pie chart click
  const handlePieClick = (data) => {
    if (data && data.hives && data.hives.length > 0) {
      // Navigate to first hive or show list
      const hiveIds = data.hives.map(h => h.id).join(',');
      navigate(`/?health=${data.name.toLowerCase()}`);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    try {
      let csvContent = "data:text/csv;charset=utf-8,";
      
      // Summary section
      csvContent += "ANALYTICS SUMMARY\n";
      csvContent += `Total Hives,${hives.length}\n`;
      csvContent += `Total Apiaries,${Object.keys(yardStats).length}\n`;
      csvContent += `Hives Being Fed,${hivesBeingFed}\n`;
      csvContent += `Total Inspections,${inspections.length}\n\n`;
      
      // Health breakdown
      csvContent += "HEALTH BREAKDOWN\n";
      csvContent += "Status,Count\n";
      healthData.forEach(h => {
        csvContent += `${h.name},${h.value}\n`;
      });
      csvContent += "\n";
      
      // Equipment by yard - use filtered data if a yard is selected
      if (filteredEquipmentSummary?.by_yard) {
        csvContent += selectedYard !== 'all' ? `EQUIPMENT FOR ${selectedYard.toUpperCase()}\n` : "EQUIPMENT BY APIARY\n";
        csvContent += "Apiary,Hives,Frames," + Object.values(filteredEquipmentSummary.stack_labels || {}).join(",") + "\n";
        Object.entries(filteredEquipmentSummary.by_yard).forEach(([yard, data]) => {
          csvContent += `${yard},${data.hive_count},${data.total_frames},`;
          csvContent += Object.keys(filteredEquipmentSummary.stack_labels || {}).map(k => data.stack?.[k] || 0).join(",");
          csvContent += "\n";
        });
        csvContent += "\n";
        
        // Components
        csvContent += "HIVE COMPONENTS BY YARD\n";
        csvContent += "Apiary," + Object.values(filteredEquipmentSummary.component_labels || {}).join(",") + "\n";
        Object.entries(filteredEquipmentSummary.by_yard).forEach(([yard, data]) => {
          csvContent += `${yard},`;
          csvContent += Object.keys(filteredEquipmentSummary.component_labels || {}).map(k => data.components?.[k] || 0).join(",");
          csvContent += "\n";
        });
      }
      
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `analytics_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  // Export Equipment Summary to CSV - respects selected yard filter
  const exportEquipmentCSV = () => {
    if (!filteredEquipmentSummary) return;
    
    let csv = '';
    
    // Company Header
    csv += `"${companyProfile?.company_name || 'Equipment Summary Report'}"\n`;
    if (companyProfile?.address) csv += `"${companyProfile.address}"\n`;
    if (companyProfile?.phone) csv += `"Phone: ${companyProfile.phone}"\n`;
    if (companyProfile?.email) csv += `"Email: ${companyProfile.email}"\n`;
    csv += `"Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}"\n`;
    if (selectedYard !== 'all') csv += `"Filtered by Apiary: ${selectedYard}"\n`;
    csv += '\n';
    
    // Field Reports Summary
    csv += '"FIELD REPORTS SUMMARY"\n';
    const filteredHives = selectedYard === 'all' ? hives : hives.filter(h => h.yard === selectedYard);
    const dueForInspection = filteredHives.filter(h => {
      if (h.status === 'ready' || h.status === 'inactive' || h.status === 'destroyed') return false;
      if (!h.last_inspection) return true;
      const days = Math.floor((new Date() - new Date(h.last_inspection)) / (1000*60*60*24));
      return days > 14;
    }).length;
    const inactiveHives = filteredHives.filter(h => h.status === 'inactive').length;
    const activeHives = filteredHives.filter(h => h.status === 'active').length;
    const readyHives = filteredHives.filter(h => h.status === 'ready').length;
    
    csv += `"Due for Inspection (14+ days)","${dueForInspection}"\n`;
    csv += `"Active Hives","${activeHives}"\n`;
    csv += `"Ready Hives","${readyHives}"\n`;
    csv += `"Inactive Hives","${inactiveHives}"\n\n`;
    
    // Transposed Stack Configuration - use filtered data
    csv += '"HIVE STACK CONFIGURATION (By Equipment Type)"\n';
    const yardNames = Object.keys(filteredEquipmentSummary.by_yard || {});
    const yardData = Object.values(filteredEquipmentSummary.by_yard || {});
    
    // Header with yard names
    if (selectedYard === 'all') {
      csv += `"Equipment",${yardNames.map(y => `"${y}"`).join(',')},"Total"\n`;
    } else {
      csv += `"Equipment","${selectedYard}"\n`;
    }
    
    // Hives row
    let total = yardData.reduce((sum, d) => sum + d.hive_count, 0);
    if (selectedYard === 'all') {
      csv += `"Hives",${yardData.map(d => d.hive_count).join(',')},"${total}"\n`;
    } else {
      csv += `"Hives","${total}"\n`;
    }
    
    // Frames row
    total = yardData.reduce((sum, d) => sum + d.total_frames, 0);
    if (selectedYard === 'all') {
      csv += `"Total Frames",${yardData.map(d => d.total_frames).join(',')},"${total}"\n`;
    } else {
      csv += `"Total Frames","${total}"\n`;
    }
    
    // Stack items
    Object.entries(filteredEquipmentSummary.stack_labels || {}).forEach(([key, label]) => {
      total = yardData.reduce((sum, d) => sum + (d.stack?.[key] || 0), 0);
      if (selectedYard === 'all') {
        csv += `"${label}",${yardData.map(d => d.stack?.[key] || 0).join(',')},"${total}"\n`;
      } else {
        csv += `"${label}","${total}"\n`;
      }
    });
    
    csv += '\n"HIVE COMPONENTS (By Component Type)"\n';
    if (selectedYard === 'all') {
      csv += `"Component",${yardNames.map(y => `"${y}"`).join(',')},"Total"\n`;
    } else {
      csv += `"Component","${selectedYard}"\n`;
    }
    
    Object.entries(filteredEquipmentSummary.component_labels || {}).forEach(([key, label]) => {
      total = yardData.reduce((sum, d) => sum + (d.components?.[key] || 0), 0);
      if (selectedYard === 'all') {
        csv += `"${label}",${yardData.map(d => d.components?.[key] || 0).join(',')},"${total}"\n`;
      } else {
        csv += `"${label}","${total}"\n`;
      }
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `equipment-summary${selectedYard !== 'all' ? '-' + selectedYard : ''}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Equipment CSV exported');
  };

  // Export Equipment Summary to PDF - respects selected yard filter
  const exportEquipmentPDF = async () => {
    if (!filteredEquipmentSummary) return;
    
    const doc = new jsPDF();
    const pageHeight = 280; // Safe printable height (leaving margin for footer)
    const pageMargin = 20; // Top margin for new pages
    let y = 15;
    
    // Helper function to check page break and add new page if needed
    const checkPageBreak = (sectionHeight) => {
      if (y + sectionHeight > pageHeight) {
        doc.addPage();
        y = pageMargin;
        return true;
      }
      return false;
    };
    
    // Helper to draw section header with consistent styling
    const drawSectionHeader = (title, color) => {
      checkPageBreak(20);
      doc.setFillColor(...color);
      doc.roundedRect(14, y - 4, 182, 10, 2, 2, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255);
      doc.text(title, 18, y + 2);
      y += 16;
    };
    
    // === LETTERHEAD ===
    let logoLoaded = false;
    if (companyProfile?.logo) {
      try {
        const logoUrl = `${API}/uploads/${companyProfile.logo}`;
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        doc.addImage(base64, 'JPEG', 14, y, 25, 25);
        logoLoaded = true;
      } catch (err) {
      }
    }
    
    // Company Name
    doc.setTextColor(0);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    const companyX = logoLoaded ? 45 : 14;
    doc.text(companyProfile?.company_name || 'Equipment Report', companyX, y + 8);
    
    if (companyProfile?.tagline) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100);
      doc.text(companyProfile.tagline, companyX, y + 15);
    }
    
    // Contact Info
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80);
    let rightY = y + 5;
    if (companyProfile?.address) { doc.text(companyProfile.address, 196, rightY, { align: 'right' }); rightY += 5; }
    if (companyProfile?.phone) { doc.text(`Phone: ${companyProfile.phone}`, 196, rightY, { align: 'right' }); rightY += 5; }
    if (companyProfile?.email) { doc.text(`Email: ${companyProfile.email}`, 196, rightY, { align: 'right' }); rightY += 5; }
    if (companyProfile?.website) { doc.text(companyProfile.website, 196, rightY, { align: 'right' }); }
    
    y += 35;
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(14, y, 196, y);
    y += 10;
    
    // Report Title
    doc.setTextColor(0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('EQUIPMENT SUMMARY REPORT', 105, y, { align: 'center' });
    y += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 105, y, { align: 'center' });
    if (selectedYard !== 'all') {
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(217, 119, 6);
      doc.text(`Filtered by Apiary: ${selectedYard}`, 105, y, { align: 'center' });
    }
    y += 15;
    
    // === FIELD REPORTS SECTION ===
    drawSectionHeader('Field Reports Summary', [59, 130, 246]); // Blue
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Calculate field report stats - use filtered hives if a yard is selected
    const filteredHivesPDF = selectedYard === 'all' ? hives : hives.filter(h => h.yard === selectedYard);
    const dueForInspection = filteredHivesPDF.filter(h => {
      if (h.status === 'ready' || h.status === 'inactive' || h.status === 'destroyed') return false;
      if (!h.last_inspection) return true;
      const days = Math.floor((new Date() - new Date(h.last_inspection)) / (1000*60*60*24));
      return days > 14;
    }).length;
    const inactiveHives = filteredHivesPDF.filter(h => h.status === 'inactive').length;
    const activeHives = filteredHivesPDF.filter(h => h.status === 'active').length;
    const readyHives = filteredHivesPDF.filter(h => h.status === 'ready').length;
    
    doc.setTextColor(60);
    doc.text(`• Due for Inspection (14+ days): ${dueForInspection} hives`, 18, y); y += 6;
    doc.text(`• Active Hives: ${activeHives}`, 18, y); y += 6;
    doc.text(`• Ready Hives: ${readyHives}`, 18, y); y += 6;
    doc.text(`• Inactive Hives: ${inactiveHives}`, 18, y); y += 12;
    
    // === HIVE STACK CONFIGURATION SECTION ===
    const stackLabels = filteredEquipmentSummary.stack_labels || {};
    const stackKeys = Object.keys(stackLabels);
    const yardNames = Object.keys(filteredEquipmentSummary.by_yard || {});
    const yardData = Object.values(filteredEquipmentSummary.by_yard || {});
    
    // Estimate section height: header (16) + header row (8) + hives row (6) + frames row (6) + stack items (stackKeys.length * 6)
    const stackSectionHeight = 36 + (stackKeys.length * 6);
    checkPageBreak(stackSectionHeight);
    
    drawSectionHeader('Hive Stack Configuration', [251, 191, 36]); // Amber/Yellow
    
    // Calculate dynamic column width based on number of yards
    const labelColWidth = 45;
    const totalColWidth = 22;
    const availableWidth = 182 - labelColWidth - totalColWidth;
    const yardColWidth = Math.min(30, Math.max(20, availableWidth / Math.max(yardNames.length, 1)));
    const maxYardNameLength = Math.floor(yardColWidth / 2.5);
    
    // Header row with yard names
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60);
    let xPos = 14;
    doc.text('Equipment', xPos, y); xPos += labelColWidth;
    yardNames.forEach(yard => {
      doc.text(yard.substring(0, maxYardNameLength), xPos, y);
      xPos += yardColWidth;
    });
    doc.text('Total', xPos, y);
    y += 4;
    
    // Draw separator line (below header text with proper spacing)
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.line(14, y, 196, y);
    y += 5;
    
    // Data rows (transposed)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    // Hives row
    xPos = 14;
    doc.setTextColor(40);
    doc.text('Hives', xPos, y); xPos += labelColWidth;
    let total = 0;
    yardData.forEach(data => {
      doc.text(String(data.hive_count), xPos, y);
      total += data.hive_count;
      xPos += yardColWidth;
    });
    doc.setFont('helvetica', 'bold');
    doc.text(String(total), xPos, y);
    y += 6;
    
    // Frames row
    doc.setFont('helvetica', 'normal');
    xPos = 14;
    doc.text('Total Frames', xPos, y); xPos += labelColWidth;
    total = 0;
    yardData.forEach(data => {
      doc.text(String(data.total_frames), xPos, y);
      total += data.total_frames;
      xPos += yardColWidth;
    });
    doc.setFont('helvetica', 'bold');
    doc.text(String(total), xPos, y);
    y += 6;
    
    // Stack items
    doc.setFont('helvetica', 'normal');
    stackKeys.forEach(key => {
      checkPageBreak(8);
      xPos = 14;
      const shortLabel = stackLabels[key].replace(' Boxes', '').replace('Deep ', 'D.').replace('Medium ', 'M.').replace('Ideal ', 'I.').replace('Shallow ', 'S.');
      doc.text(shortLabel.substring(0, 20), xPos, y); xPos += labelColWidth;
      total = 0;
      yardData.forEach(data => {
        const val = data.stack?.[key] || 0;
        doc.text(String(val), xPos, y);
        total += val;
        xPos += yardColWidth;
      });
      doc.setFont('helvetica', 'bold');
      doc.text(String(total), xPos, y);
      doc.setFont('helvetica', 'normal');
      y += 6;
    });
    y += 12;
    
    // === YARD TOOLS SECTION ===
    // Use filteredYards instead of all yards to respect the yard filter
    const yardsForTools = filteredYards || [];
    // Build tool items dynamically from API tool_labels (merged hardcoded + admin-configured)
    const toolLabels = filteredEquipmentSummary.tool_labels || {};
    const toolItems = Object.entries(toolLabels).map(([key, label]) => ({ key, label }));
    
    // Estimate section height for page break check
    const toolsSectionHeight = 20 + (toolItems.length * 5);
    checkPageBreak(Math.min(toolsSectionHeight, 100)); // Check if at least partial section fits
    
    drawSectionHeader('Apiary Tools', [217, 119, 6]); // Amber
    
    // Calculate dynamic column width for yards
    const toolLabelWidth = 40;
    const toolTotalWidth = 20;
    const toolAvailWidth = 182 - toolLabelWidth - toolTotalWidth;
    const toolYardColWidth = Math.min(30, Math.max(18, toolAvailWidth / Math.max(yardsForTools.length, 1)));
    const toolMaxYardLen = Math.floor(toolYardColWidth / 2.5);
    
    // Header row with yard names
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60);
    xPos = 14;
    doc.text('Item', xPos, y); xPos += toolLabelWidth;
    yardsForTools.forEach(yard => {
      doc.text((yard.name || '').substring(0, toolMaxYardLen), xPos, y);
      xPos += toolYardColWidth;
    });
    doc.text('Total', xPos, y);
    y += 4;
    
    // Draw separator line (below header text with proper spacing)
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.line(14, y, 196, y);
    y += 5;
    
    // Tools rows
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    toolItems.forEach(({ key, label }) => {
      checkPageBreak(8);
      xPos = 14;
      doc.setTextColor(40);
      doc.text(label.substring(0, 18), xPos, y); xPos += toolLabelWidth;
      let total = 0;
      yardsForTools.forEach(yard => {
        const val = yard.tools?.[key] || 0;
        doc.setTextColor(60);
        doc.text(String(val), xPos, y);
        total += val;
        xPos += toolYardColWidth;
      });
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40);
      doc.text(String(total), xPos, y);
      doc.setFont('helvetica', 'normal');
      y += 5;
    });
    y += 12;
    
    // === HIVE COMPONENTS SECTION (TRANSPOSED - Components as rows, Yards as columns) ===
    const compLabels = filteredEquipmentSummary.component_labels || {};
    const compKeys = Object.keys(compLabels);
    const compYardNames = Object.keys(filteredEquipmentSummary.by_yard || {});
    const compYardData = Object.values(filteredEquipmentSummary.by_yard || {});
    
    // Only add section if there are components
    if (compKeys.length > 0) {
      // Estimate section height for page break check
      const compSectionHeight = 20 + (compKeys.length * 6);
      checkPageBreak(Math.min(compSectionHeight, 80));
      
      drawSectionHeader('Hive Components by Apiary', [100, 116, 139]); // Slate
      
      // Calculate dynamic column width based on number of yards (same as Stack section)
      const compLabelColWidth = 45;
      const compTotalColWidth = 22;
      const compAvailWidth = 182 - compLabelColWidth - compTotalColWidth;
      const compYardColWidth = Math.min(30, Math.max(20, compAvailWidth / Math.max(compYardNames.length, 1)));
      const compMaxYardLen = Math.floor(compYardColWidth / 2.5);
      
      // Header row with yard names (transposed - yards as columns)
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60);
      xPos = 14;
      doc.text('Component', xPos, y); xPos += compLabelColWidth;
      compYardNames.forEach(yard => {
        doc.text(yard.substring(0, compMaxYardLen), xPos, y);
        xPos += compYardColWidth;
      });
      doc.text('Total', xPos, y);
      y += 8;
      
      // Draw separator line (with proper spacing)
      doc.setDrawColor(200);
      doc.setLineWidth(0.3);
      doc.line(14, y - 3, 196, y - 3);
      
      // Data rows (transposed - components as rows)
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      compKeys.forEach(key => {
        checkPageBreak(8);
        xPos = 14;
        const shortLabel = compLabels[key]
          .replace('Screened Bottom Board', 'SBB')
          .replace('w/ Tray', '+T')
          .replace('Entrance', 'Ent.')
          .replace('Reducer', 'Red.')
          .substring(0, 22);
        doc.setTextColor(40);
        doc.text(shortLabel, xPos, y); xPos += compLabelColWidth;
        let total = 0;
        compYardData.forEach(data => {
          const val = data.components?.[key] || 0;
          doc.setTextColor(60);
          doc.text(String(val), xPos, y);
          total += val;
          xPos += compYardColWidth;
        });
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40);
        doc.text(String(total), xPos, y);
        doc.setFont('helvetica', 'normal');
        y += 6;
      });
      y += 10;
    }
    
    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
      doc.text(companyProfile?.company_name || '', 14, 290);
      doc.text(new Date().toLocaleDateString(), 196, 290, { align: 'right' });
    }
    
    doc.save(`equipment-summary${selectedYard !== 'all' ? '-' + selectedYard : ''}-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Equipment PDF exported');
  };

  // Export to PDF
  const exportToPDF = async () => {
    setExporting(true);
    try {
      const element = analyticsRef.current;
      if (!element) {
        toast.error('Unable to export PDF');
        return;
      }
      
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#fafaf9',
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = 210;
      const pageHeight = 297;
      const marginX = 10;
      const marginY = 10;
      const printableWidth = pageWidth - (marginX * 2);
      const printableHeight = pageHeight - (marginY * 2);
      
      const imgWidth = printableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add title
      pdf.setFontSize(18);
      pdf.text('Analytics Report', marginX, marginY + 5);
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, marginX, marginY + 12);
      
      let yPosition = marginY + 20;
      
      if (imgHeight <= printableHeight - 20) {
        pdf.addImage(imgData, 'PNG', marginX, yPosition, imgWidth, imgHeight);
      } else {
        // Multi-page handling
        let pageNum = 0;
        let heightLeft = imgHeight;
        
        while (heightLeft > 0) {
          if (pageNum > 0) {
            pdf.addPage();
            yPosition = marginY;
          }
          
          const sliceHeight = Math.min(printableHeight - (pageNum === 0 ? 20 : 0), heightLeft);
          const sourceY = (imgHeight - heightLeft) / imgHeight * canvas.height;
          const sourceHeight = sliceHeight / imgHeight * canvas.height;
          
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = sourceHeight;
          const ctx = pageCanvas.getContext('2d');
          ctx.fillStyle = '#fafaf9';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
          
          const pageImgData = pageCanvas.toDataURL('image/png');
          pdf.addImage(pageImgData, 'PNG', marginX, yPosition, imgWidth, sliceHeight);
          
          heightLeft -= sliceHeight;
          pageNum++;
        }
      }
      
      pdf.save(`analytics_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Export Cost Summary to CSV
  const exportCostCSV = () => {
    if (!costSummary) return;
    
    try {
      let csv = '';
      
      // Calculate harvest equipment investment
      const harvestEquipmentInvestment = harvestConfig?.harvesting_equipment?.reduce((sum, e) => {
        return sum + ((parseFloat(e.qty) || 0) * (parseFloat(e.price) || 0));
      }, 0) || 0;
      const totalWithHarvestEquip = (costSummary.grand_total || 0) + harvestEquipmentInvestment;
      
      // Calculate revenue
      let totalRevenue = 0;
      let soldRevenue = 0;
      let potentialRevenue = 0;
      (harvests || []).forEach(h => {
        const revenue = (parseFloat(h.quantity) || 0) * (parseFloat(h.sale_price) || 0);
        totalRevenue += revenue;
        if (h.is_sold) soldRevenue += revenue;
        else potentialRevenue += revenue;
      });
      const netProfit = soldRevenue - totalWithHarvestEquip;
      
      // Header
      csv += `"COST SUMMARY REPORT"\n`;
      csv += `"${companyProfile?.company_name || 'Beekeeping Operation'}"\n`;
      csv += `"Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}"\n`;
      if (selectedYard !== 'all') csv += `"Filtered by Apiary: ${selectedYard}"\n`;
      csv += '\n';
      
      // Grand Total (including harvest equipment)
      csv += `"GRAND TOTAL (All Investments)","$${totalWithHarvestEquip.toFixed(2)}"\n`;
      csv += `"  - Apiary & Hive Costs","$${(costSummary.grand_total || 0).toFixed(2)}"\n`;
      if (harvestEquipmentInvestment > 0) {
        csv += `"  - Harvesting Equipment","$${harvestEquipmentInvestment.toFixed(2)}"\n`;
      }
      csv += '\n';
      
      // Revenue Section
      if (totalRevenue > 0) {
        csv += '"REVENUE SUMMARY"\n';
        csv += `"Total Revenue","$${totalRevenue.toFixed(2)}"\n`;
        csv += `"  - Sold Revenue","$${soldRevenue.toFixed(2)}"\n`;
        csv += `"  - Potential (In Stock)","$${potentialRevenue.toFixed(2)}"\n`;
        csv += `"Net Profit (Sold - Investment)","$${netProfit.toFixed(2)}"\n\n`;
      }
      
      // Cost by Apiary
      csv += '"COST BY YARD"\n';
      csv += '"Apiary","Apiary Equipment","Hive Costs","Total"\n';
      
      Object.entries(costSummary.by_yard || {})
        .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard)
        .forEach(([yardName, data]) => {
          csv += `"${yardName}","$${(data.yard_equipment_cost || 0).toFixed(2)}","$${(data.total_hive_cost || 0).toFixed(2)}","$${(data.total_cost || 0).toFixed(2)}"\n`;
        });
      
      // Totals row
      const filteredYards = Object.entries(costSummary.by_yard || {})
        .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard);
      const totalYardEquip = filteredYards.reduce((sum, [_, d]) => sum + (d.yard_equipment_cost || 0), 0);
      const totalHiveCost = filteredYards.reduce((sum, [_, d]) => sum + (d.total_hive_cost || 0), 0);
      const totalCost = filteredYards.reduce((sum, [_, d]) => sum + (d.total_cost || 0), 0);
      csv += `"TOTAL","$${totalYardEquip.toFixed(2)}","$${totalHiveCost.toFixed(2)}","$${totalCost.toFixed(2)}"\n\n`;
      
      // Hive-level breakdown
      csv += '"HIVE-LEVEL COST BREAKDOWN"\n';
      csv += '"Apiary","Hive #","Nickname","Bee Purchase","Equipment","Total"\n';
      
      Object.entries(costSummary.by_yard || {})
        .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard)
        .forEach(([yardName, data]) => {
          (data.hive_costs || []).forEach(hive => {
            if (hive.total_cost > 0) {
              csv += `"${yardName}","Hive ${hive.hive_number}","${hive.nickname || ''}","$${(hive.origin_price || 0).toFixed(2)}","$${(hive.equipment_cost || 0).toFixed(2)}","$${(hive.total_cost || 0).toFixed(2)}"\n`;
            }
          });
        });
      
      // Harvesting Equipment breakdown
      if (harvestConfig?.harvesting_equipment && harvestConfig.harvesting_equipment.some(e => (parseInt(e.qty) || 0) > 0)) {
        csv += '\n"HARVESTING EQUIPMENT INVESTMENT"\n';
        csv += '"Equipment","Quantity","Unit Price","Subtotal"\n';
        harvestConfig.harvesting_equipment.forEach(equip => {
          const qty = parseInt(equip.qty) || 0;
          const price = parseFloat(equip.price) || 0;
          if (qty > 0) {
            csv += `"${equip.name}","${qty}","$${price.toFixed(2)}","$${(qty * price).toFixed(2)}"\n`;
          }
        });
        csv += `"TOTAL","","","$${harvestEquipmentInvestment.toFixed(2)}"\n`;
      }
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `cost-summary${selectedYard !== 'all' ? '-' + selectedYard : ''}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Cost CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export cost CSV');
    }
  };

  // Export Cost Summary to PDF
  const exportCostPDF = () => {
    if (!costSummary) return;
    
    try {
      const doc = new jsPDF();
      let yPos = 20;
      
      // Calculate harvest equipment investment
      const harvestEquipmentInvestment = harvestConfig?.harvesting_equipment?.reduce((sum, e) => {
        return sum + ((parseFloat(e.qty) || 0) * (parseFloat(e.price) || 0));
      }, 0) || 0;
      const totalWithHarvestEquip = (costSummary.grand_total || 0) + harvestEquipmentInvestment;
      
      // Calculate revenue
      let totalRevenue = 0;
      let soldRevenue = 0;
      let potentialRevenue = 0;
      (harvests || []).forEach(h => {
        const revenue = (parseFloat(h.quantity) || 0) * (parseFloat(h.sale_price) || 0);
        totalRevenue += revenue;
        if (h.is_sold) soldRevenue += revenue;
        else potentialRevenue += revenue;
      });
      const netProfit = soldRevenue - totalWithHarvestEquip;
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(34, 139, 34); // Forest green
      doc.text('Cost Summary Report', 14, yPos);
      yPos += 10;
      
      // Company name
      doc.setFontSize(12);
      doc.setTextColor(0);
      if (companyProfile?.company_name) {
        doc.text(companyProfile.company_name, 14, yPos);
        yPos += 6;
      }
      
      // Date
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 14, yPos);
      if (selectedYard !== 'all') {
        yPos += 5;
        doc.text(`Filtered by Apiary: ${selectedYard}`, 14, yPos);
      }
      yPos += 15;
      
      // Grand Total Box (including harvest equipment)
      doc.setFillColor(220, 252, 231); // Light green
      doc.roundedRect(14, yPos - 5, 182, harvestEquipmentInvestment > 0 ? 25 : 15, 3, 3, 'F');
      doc.setFontSize(14);
      doc.setTextColor(21, 128, 61); // Green
      doc.text('Total Investment:', 20, yPos + 5);
      doc.setFontSize(16);
      doc.text(`$${totalWithHarvestEquip.toFixed(2)}`, 180, yPos + 5, { align: 'right' });
      
      // Breakdown if harvest equipment exists
      if (harvestEquipmentInvestment > 0) {
        yPos += 10;
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`Apiary & Hive Costs: $${(costSummary.grand_total || 0).toFixed(2)}  |  Harvesting Equipment: $${harvestEquipmentInvestment.toFixed(2)}`, 20, yPos + 5);
        yPos += 10;
      }
      yPos += 15;
      
      // Revenue Box (if revenue exists)
      if (totalRevenue > 0) {
        doc.setFillColor(209, 250, 229); // Emerald-100
        doc.roundedRect(14, yPos - 5, 182, 30, 3, 3, 'F');
        doc.setFontSize(12);
        doc.setTextColor(6, 95, 70); // Emerald-800
        doc.text('Revenue Summary', 20, yPos + 3);
        doc.setFontSize(14);
        doc.text(`Total Revenue: $${totalRevenue.toFixed(2)}`, 180, yPos + 3, { align: 'right' });
        yPos += 10;
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`Sold: $${soldRevenue.toFixed(2)}  |  In Stock: $${potentialRevenue.toFixed(2)}  |  Net Profit: $${netProfit.toFixed(2)}`, 20, yPos + 3);
        yPos += 25;
      }
      
      // Cost by Apiary Table
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Cost by Apiary', 14, yPos);
      yPos += 8;
      
      // Table Header
      doc.setFillColor(34, 139, 34);
      doc.rect(14, yPos - 4, 182, 8, 'F');
      doc.setTextColor(255);
      doc.setFontSize(10);
      doc.text('Apiary', 18, yPos);
      doc.text('Apiary Equipment', 80, yPos);
      doc.text('Hive Costs', 125, yPos);
      doc.text('Total', 165, yPos);
      yPos += 8;
      
      // Table Rows
      doc.setTextColor(0);
      let isAlternate = false;
      
      Object.entries(costSummary.by_yard || {})
        .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard)
        .forEach(([yardName, data]) => {
          if (isAlternate) {
            doc.setFillColor(240, 253, 244);
            doc.rect(14, yPos - 4, 182, 7, 'F');
          }
          doc.text(yardName.substring(0, 25), 18, yPos);
          doc.text(`$${(data.yard_equipment_cost || 0).toFixed(2)}`, 80, yPos);
          doc.text(`$${(data.total_hive_cost || 0).toFixed(2)}`, 125, yPos);
          doc.text(`$${(data.total_cost || 0).toFixed(2)}`, 165, yPos);
          yPos += 7;
          isAlternate = !isAlternate;
          
          // Check for page break
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
        });
      
      // Total Row
      const filteredYards = Object.entries(costSummary.by_yard || {})
        .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard);
      const totalYardEquip = filteredYards.reduce((sum, [_, d]) => sum + (d.yard_equipment_cost || 0), 0);
      const totalHiveCost = filteredYards.reduce((sum, [_, d]) => sum + (d.total_hive_cost || 0), 0);
      const totalCost = filteredYards.reduce((sum, [_, d]) => sum + (d.total_cost || 0), 0);
      
      doc.setFillColor(187, 247, 208);
      doc.rect(14, yPos - 4, 182, 8, 'F');
      doc.setFontSize(11);
      doc.setTextColor(21, 128, 61);
      doc.text('TOTAL', 18, yPos);
      doc.text(`$${totalYardEquip.toFixed(2)}`, 80, yPos);
      doc.text(`$${totalHiveCost.toFixed(2)}`, 125, yPos);
      doc.text(`$${totalCost.toFixed(2)}`, 165, yPos);
      yPos += 15;
      
      // Hive-level breakdown (if any have costs)
      const hivesWithCosts = Object.entries(costSummary.by_yard || {})
        .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard)
        .flatMap(([yardName, data]) => 
          (data.hive_costs || []).filter(h => h.total_cost > 0).map(h => ({ ...h, yardName }))
        );
      
      if (hivesWithCosts.length > 0) {
        // Check for page break
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text('Hive-Level Cost Breakdown', 14, yPos);
        yPos += 8;
        
        // Mini table header
        doc.setFillColor(34, 139, 34);
        doc.rect(14, yPos - 4, 182, 7, 'F');
        doc.setTextColor(255);
        doc.setFontSize(9);
        doc.text('Apiary', 18, yPos);
        doc.text('Hive', 60, yPos);
        doc.text('Bee Purchase', 100, yPos);
        doc.text('Equipment', 135, yPos);
        doc.text('Total', 170, yPos);
        yPos += 7;
        
        doc.setTextColor(0);
        isAlternate = false;
        
        hivesWithCosts.forEach(hive => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          
          if (isAlternate) {
            doc.setFillColor(240, 253, 244);
            doc.rect(14, yPos - 4, 182, 6, 'F');
          }
          doc.text(hive.yardName.substring(0, 15), 18, yPos);
          doc.text(`#${hive.hive_number}${hive.nickname ? ' ' + hive.nickname.substring(0, 10) : ''}`, 60, yPos);
          doc.text(`$${(hive.origin_price || 0).toFixed(2)}`, 100, yPos);
          doc.text(`$${(hive.equipment_cost || 0).toFixed(2)}`, 135, yPos);
          doc.text(`$${(hive.total_cost || 0).toFixed(2)}`, 170, yPos);
          yPos += 6;
          isAlternate = !isAlternate;
        });
      }
      
      // Harvesting Equipment Section
      const equipmentWithQty = harvestConfig?.harvesting_equipment?.filter(e => (parseInt(e.qty) || 0) > 0) || [];
      if (equipmentWithQty.length > 0) {
        // Check for page break
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }
        
        yPos += 10;
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text('Harvesting Equipment Investment', 14, yPos);
        yPos += 8;
        
        // Table header - orange theme
        doc.setFillColor(234, 88, 12); // Orange-600
        doc.rect(14, yPos - 4, 182, 7, 'F');
        doc.setTextColor(255);
        doc.setFontSize(9);
        doc.text('Equipment', 18, yPos);
        doc.text('Qty', 110, yPos);
        doc.text('Unit Price', 135, yPos);
        doc.text('Subtotal', 170, yPos);
        yPos += 7;
        
        doc.setTextColor(0);
        isAlternate = false;
        
        equipmentWithQty.forEach(equip => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          
          const qty = parseInt(equip.qty) || 0;
          const price = parseFloat(equip.price) || 0;
          const subtotal = qty * price;
          
          if (isAlternate) {
            doc.setFillColor(255, 237, 213); // Orange-100
            doc.rect(14, yPos - 4, 182, 6, 'F');
          }
          doc.text(equip.name.substring(0, 40), 18, yPos);
          doc.text(String(qty), 110, yPos);
          doc.text(`$${price.toFixed(2)}`, 135, yPos);
          doc.text(`$${subtotal.toFixed(2)}`, 170, yPos);
          yPos += 6;
          isAlternate = !isAlternate;
        });
        
        // Total row
        doc.setFillColor(251, 191, 36); // Amber-400
        doc.rect(14, yPos - 4, 182, 7, 'F');
        doc.setFontSize(10);
        doc.setTextColor(120, 53, 15); // Orange-900
        doc.text('TOTAL', 18, yPos);
        doc.text(`$${harvestEquipmentInvestment.toFixed(2)}`, 170, yPos);
        yPos += 10;
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
      }
      
      doc.save(`cost-summary${selectedYard !== 'all' ? '-' + selectedYard : ''}-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Cost PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export cost PDF');
    }
  };

  const queenRearingData = useMemo(() => {
    if (!queenRearingRaw) return null;
    const hiveYard = (id) => (id && hives.find(h => h.id === id)?.yard) || '';
    const yardName = (id) => (id && yards.find(y => y.id === id)?.name) || '';
    const inYard = (x) => selectedYard === 'all' || x.yard_name === selectedYard;
    const allBatches = queenRearingRaw.batches.map(b => ({ ...b, yard_name: b.yard_name || hiveYard(b.cell_builder_hive_id) || hiveYard(b.breeder_hive_id) || hiveYard(b.finisher_hive_id) }));
    const batches = allBatches.filter(inYard);
    const breeders = queenRearingRaw.breeders.map(b => ({ ...b, yard_name: b.yard_name || hiveYard(b.source_hive_id) })).filter(inYard);
    const nucs = queenRearingRaw.nucs.map(n => ({ ...n, yard_name: n.yard_name || yardName(n.yard_id) || n.location || '' })).filter(inYard);
    const countCells = (pred) => batches.reduce((sum, b) => sum + (b.cells || []).filter(pred).length, 0);
    const totalCells = batches.reduce((sum, b) => sum + (b.cells_grafted || 0), 0);
    const cellsLaying = countCells(c => c.status === 'laying');
    return {
      batches, breeders, nucs, allBatches,
      stats: {
        totalBatches: batches.length,
        activeBatches: batches.filter(b => b.status !== 'complete').length,
        completedBatches: batches.filter(b => b.status === 'complete').length,
        totalBreeders: breeders.length,
        activeBreeders: breeders.filter(b => b.status === 'active').length,
        totalNucs: nucs.length,
        availableNucs: nucs.filter(n => n.status === 'available').length,
        occupiedNucs: nucs.filter(n => n.status === 'occupied').length,
        totalCells,
        cellsLaying,
        cellsEmerged: countCells(c => ['emerged', 'mated', 'laying'].includes(c.status)),
        cellsFailed: countCells(c => c.status === 'failed'),
        successRate: totalCells > 0 ? Math.round((cellsLaying / totalCells) * 100) : 0
      }
    };
  }, [queenRearingRaw, hives, yards, selectedYard]);

  // Export Queen Rearing to CSV
  const exportQueenRearingCSV = () => {
    if (!queenRearingData) return;
    
    try {
      let csv = '';
      const { batches, breeders, nucs, stats } = queenRearingData;
      
      // Header
      csv += `"${companyProfile?.company_name || 'Queen Rearing Report'}"\n`;
      csv += `"Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}"\n`;
      csv += `"Apiary","${selectedYard === 'all' ? 'All Apiaries' : selectedYard}"\n\n`;
      
      // Summary Stats
      csv += '"QUEEN REARING SUMMARY"\n';
      csv += `"Total Batches","${stats.totalBatches}"\n`;
      csv += `"Active Batches","${stats.activeBatches}"\n`;
      csv += `"Completed Batches","${stats.completedBatches}"\n`;
      csv += `"Total Breeder Queens","${stats.totalBreeders}"\n`;
      csv += `"Active Breeders","${stats.activeBreeders}"\n`;
      csv += `"Total Mating Nucs","${stats.totalNucs}"\n`;
      csv += `"Available Nucs","${stats.availableNucs}"\n`;
      csv += `"Occupied Nucs","${stats.occupiedNucs}"\n\n`;
      
      // Cell Statistics
      csv += '"CELL STATISTICS"\n';
      csv += `"Total Cells Grafted","${stats.totalCells}"\n`;
      csv += `"Cells Emerged","${stats.cellsEmerged}"\n`;
      csv += `"Queens Laying","${stats.cellsLaying}"\n`;
      csv += `"Cells Failed","${stats.cellsFailed}"\n`;
      csv += `"Success Rate","${stats.successRate}%"\n\n`;
      
      // Apiary comparison
      csv += '"APIARY QUEEN COMPARISON"\n';
      csv += '"Apiary","Batches","Cells Grafted","Emerged","Laying","Failed","Success Rate"\n';
      buildApiaryQueenStats(queenRearingData.allBatches).forEach(a => {
        csv += `"${a.apiary}","${a.batches}","${a.grafted}","${a.emerged}","${a.laying}","${a.failed}","${a.successRate}%"\n`;
      });
      csv += '\n';
      
      // Batches Detail
      csv += '"BATCH DETAILS"\n';
      csv += '"Batch Name","Apiary","Graft Date","Breeder Queen","Cell Starter Hive","Finisher Hive","Cells Grafted","Status","Grafting Tool","Cell Cups"\n';
      batches.forEach(b => {
        csv += `"${b.batch_name || 'Batch #' + b.batch_number}","${b.yard_name || ''}","${b.graft_date || ''}","${b.breeder_queen_name || ''}","${b.cell_builder_hive_number ? 'Hive #' + b.cell_builder_hive_number : ''}","${b.finisher_hive_number ? 'Hive #' + b.finisher_hive_number : (b.cell_builder_hive_number ? 'Same as starter' : '')}","${b.cells_grafted || 0}","${b.status || ''}","${b.grafting_tool || ''}","${b.cell_cups || ''}"\n`;
      });
      csv += '\n';
      
      // Breeder Queens
      csv += '"BREEDER QUEENS"\n';
      csv += '"Name","Apiary","Genetics","Status","Source Hive","Acquired Date","Notes"\n';
      breeders.forEach(b => {
        csv += `"${b.name || ''}","${b.yard_name || ''}","${b.genetics || ''}","${b.status || ''}","${b.source_hive_number ? 'Hive #' + b.source_hive_number : ''}","${b.acquired_date || ''}","${b.notes || ''}"\n`;
      });
      csv += '\n';
      
      // Mating Nucs
      csv += '"MATING NUCS"\n';
      csv += '"Nuc Number","Apiary","Type","Location","Status","Notes"\n';
      nucs.forEach(n => {
        csv += `"${n.nuc_number || ''}","${n.yard_name || ''}","${n.nuc_type || ''}","${n.location || ''}","${n.status || ''}","${n.notes || ''}"\n`;
      });
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `queen-rearing-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Queen Rearing CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export Queen Rearing CSV');
    }
  };

  // Export Queen Rearing to PDF
  const exportQueenRearingPDF = () => {
    if (!queenRearingData) return;
    
    try {
      const doc = new jsPDF();
      const { batches, breeders, nucs, stats } = queenRearingData;
      let yPos = 20;
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(180, 83, 9); // Amber color
      doc.text('Queen Rearing Report', 14, yPos);
      yPos += 10;
      
      // Company name
      doc.setFontSize(12);
      doc.setTextColor(0);
      if (companyProfile?.company_name) {
        doc.text(companyProfile.company_name, 14, yPos);
        yPos += 6;
      }
      
      // Date
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 14, yPos);
      yPos += 6;
      doc.text(`Apiary: ${selectedYard === 'all' ? 'All Apiaries' : selectedYard}`, 14, yPos);
      yPos += 9;
      
      // Summary Stats - Two columns
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Summary Statistics', 14, yPos);
      yPos += 8;
      
      // Draw stats boxes
      doc.setFillColor(254, 243, 199); // Amber-100
      doc.rect(14, yPos, 55, 25, 'F');
      doc.rect(75, yPos, 55, 25, 'F');
      doc.rect(136, yPos, 55, 25, 'F');
      
      doc.setFontSize(18);
      doc.setTextColor(180, 83, 9);
      doc.text(String(stats.activeBatches), 41, yPos + 12, { align: 'center' });
      doc.text(String(stats.activeBreeders), 102, yPos + 12, { align: 'center' });
      doc.text(String(stats.totalNucs), 163, yPos + 12, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text('Active Batches', 41, yPos + 20, { align: 'center' });
      doc.text('Active Breeders', 102, yPos + 20, { align: 'center' });
      doc.text('Mating Nucs', 163, yPos + 20, { align: 'center' });
      yPos += 35;
      
      // Cell Statistics
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Cell Statistics', 14, yPos);
      yPos += 8;
      
      doc.setFillColor(254, 249, 195); // Yellow-100
      doc.rect(14, yPos, 40, 20, 'F');
      doc.rect(60, yPos, 40, 20, 'F');
      doc.rect(106, yPos, 40, 20, 'F');
      doc.rect(152, yPos, 40, 20, 'F');
      
      doc.setFontSize(14);
      doc.setTextColor(133, 77, 14);
      doc.text(String(stats.totalCells), 34, yPos + 10, { align: 'center' });
      doc.text(String(stats.cellsEmerged), 80, yPos + 10, { align: 'center' });
      doc.text(String(stats.cellsLaying), 126, yPos + 10, { align: 'center' });
      doc.text(`${stats.successRate}%`, 172, yPos + 10, { align: 'center' });
      
      doc.setFontSize(8);
      doc.text('Total Grafted', 34, yPos + 16, { align: 'center' });
      doc.text('Emerged', 80, yPos + 16, { align: 'center' });
      doc.text('Laying', 126, yPos + 16, { align: 'center' });
      doc.text('Success Rate', 172, yPos + 16, { align: 'center' });
      yPos += 30;
      
      // Apiary comparison
      const apiaryStats = buildApiaryQueenStats(queenRearingData.allBatches);
      if (apiaryStats.length > 0) {
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text('Apiary Queen Comparison', 14, yPos);
        yPos += 7;
        apiaryStats.forEach((a, i) => {
          const barW = Math.max(1, (a.successRate / 100) * 90);
          doc.setFontSize(9);
          doc.setTextColor(60);
          doc.text(`${a.apiary}`.substring(0, 22), 14, yPos + 4);
          doc.setFillColor(245, 245, 244);
          doc.rect(60, yPos, 90, 5, 'F');
          if (i === 0 && a.successRate > 0) doc.setFillColor(217, 119, 6); else doc.setFillColor(252, 211, 77);
          doc.rect(60, yPos, barW, 5, 'F');
          doc.text(`${a.successRate}%  (${a.laying}/${a.grafted})${i === 0 && a.successRate > 0 ? '  Best' : ''}`, 153, yPos + 4);
          yPos += 8;
        });
        yPos += 6;
      }
      
      // Batches Table
      if (batches.length > 0) {
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text('Batch Details', 14, yPos);
        yPos += 8;
        
        // Table header
        doc.setFillColor(180, 83, 9);
        doc.rect(14, yPos - 4, 182, 7, 'F');
        doc.setTextColor(255);
        doc.setFontSize(9);
        doc.text('Batch Name', 16, yPos);
        doc.text('Apiary', 50, yPos);
        doc.text('Graft Date', 80, yPos);
        doc.text('Breeder', 104, yPos);
        doc.text('Starter', 132, yPos);
        doc.text('Finisher', 150, yPos);
        doc.text('Cells', 168, yPos);
        doc.text('Status', 180, yPos);
        yPos += 7;
        
        doc.setTextColor(0);
        let isAlternate = false;
        
        batches.forEach(b => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          
          if (isAlternate) {
            doc.setFillColor(254, 249, 195);
            doc.rect(14, yPos - 4, 182, 6, 'F');
          }
          
          doc.setFontSize(9);
          doc.setFontSize(8);
          doc.text((b.batch_name || `Batch #${b.batch_number}`).substring(0, 18), 16, yPos);
          doc.text((b.yard_name || '-').substring(0, 15), 50, yPos);
          doc.text(b.graft_date || '-', 80, yPos);
          doc.text((b.breeder_queen_name || '-').substring(0, 14), 104, yPos);
          doc.text(b.cell_builder_hive_number ? `#${b.cell_builder_hive_number}` : '-', 132, yPos);
          doc.text(b.finisher_hive_number ? `#${b.finisher_hive_number}` : (b.cell_builder_hive_number ? 'Same' : '-'), 150, yPos);
          doc.text(String(b.cells_grafted || 0), 168, yPos);
          doc.text((b.status || '-').substring(0, 9), 180, yPos);
          yPos += 6;
          isAlternate = !isAlternate;
        });
        yPos += 10;
      }
      
      // Breeder Queens
      if (breeders.length > 0 && yPos < 240) {
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text('Breeder Queens', 14, yPos);
        yPos += 8;
        
        doc.setFillColor(147, 51, 234); // Purple
        doc.rect(14, yPos - 4, 182, 7, 'F');
        doc.setTextColor(255);
        doc.setFontSize(9);
        doc.text('Name', 18, yPos);
        doc.text('Apiary', 65, yPos);
        doc.text('Genetics', 105, yPos);
        doc.text('Status', 135, yPos);
        doc.text('Source Hive', 160, yPos);
        yPos += 7;
        
        doc.setTextColor(0);
        let isAlternate = false;
        
        breeders.forEach(b => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          
          if (isAlternate) {
            doc.setFillColor(243, 232, 255);
            doc.rect(14, yPos - 4, 182, 6, 'F');
          }
          
          doc.text((b.name || '-').substring(0, 22), 18, yPos);
          doc.text((b.yard_name || '-').substring(0, 18), 65, yPos);
          doc.text((b.genetics || '-').substring(0, 14), 105, yPos);
          doc.text(b.status || '-', 135, yPos);
          doc.text(b.source_hive_number ? `Hive #${b.source_hive_number}` : '-', 160, yPos);
          yPos += 6;
          isAlternate = !isAlternate;
        });
        yPos += 10;
      }
      
      // Mating Nucs
      if (nucs.length > 0) {
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text('Mating Nucs', 14, yPos);
        yPos += 8;
        
        doc.setFillColor(37, 99, 235); // Blue
        doc.rect(14, yPos - 4, 182, 7, 'F');
        doc.setTextColor(255);
        doc.setFontSize(9);
        doc.text('Nuc #', 18, yPos);
        doc.text('Type', 50, yPos);
        doc.text('Location', 100, yPos);
        doc.text('Status', 160, yPos);
        yPos += 7;
        
        doc.setTextColor(0);
        let isAlternate = false;
        
        nucs.forEach(n => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          
          if (isAlternate) {
            doc.setFillColor(219, 234, 254);
            doc.rect(14, yPos - 4, 182, 6, 'F');
          }
          
          doc.text(String(n.nuc_number || '-'), 18, yPos);
          doc.text((n.nuc_type || '-').substring(0, 15), 50, yPos);
          doc.text((n.location || '-').substring(0, 20), 100, yPos);
          doc.text(n.status || '-', 160, yPos);
          yPos += 6;
          isAlternate = !isAlternate;
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
      }
      
      doc.save(`queen-rearing-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Queen Rearing PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export Queen Rearing PDF');
    }
  };

  const getYardCoordinates = (yardName) => {
    // First check geocoded coordinates from address (from backend API)
    if (geocodedYards[yardName]) {
      return [geocodedYards[yardName].lat, geocodedYards[yardName].lng];
    }
    // Fallback to GPS if stored in yard data (validate coordinates are reasonable)
    const yard = yards.find(y => y.name === yardName);
    if (yard && yard.gps_lat && yard.gps_lng) {
      // Validate coordinates are within reasonable bounds
      if (Math.abs(yard.gps_lat) <= 90 && Math.abs(yard.gps_lng) <= 180 &&
          yard.gps_lat !== 0 && yard.gps_lng !== 0) {
        return [yard.gps_lat, yard.gps_lng];
      }
    }
    return null; // Return null if no valid coordinates available
  };

  // Compute filtered equipment summary based on selected yard
  const getFilteredEquipmentSummary = () => {
    if (!equipmentSummary) return null;
    if (selectedYard === 'all') return equipmentSummary;
    
    // Filter to show only the selected yard
    const filteredByYard = {};
    if (equipmentSummary.by_yard && equipmentSummary.by_yard[selectedYard]) {
      filteredByYard[selectedYard] = equipmentSummary.by_yard[selectedYard];
    }
    
    return {
      ...equipmentSummary,
      by_yard: filteredByYard
    };
  };
  
  const filteredEquipmentSummary = getFilteredEquipmentSummary();
  
  // Filter hives and inspections based on selected yard
  const filteredHives = selectedYard === 'all' ? hives : hives.filter(h => h.yard === selectedYard);
  const filteredInspections = selectedYard === 'all' ? inspections : inspections.filter(i => {
    const hive = hives.find(h => h.id === i.hive_id);
    return hive && hive.yard === selectedYard;
  });

  // Pre-compute the latest inspection per hive (using date + created_at as tiebreaker)
  const latestInspectionIds = new Set();
  const latestByHive = {};
  filteredInspections.forEach(insp => {
    const existing = latestByHive[insp.hive_id];
    if (!existing) {
      latestByHive[insp.hive_id] = insp;
    } else {
      const existingDate = new Date(existing.date || existing.created_at);
      const currentDate = new Date(insp.date || insp.created_at);
      if (currentDate > existingDate) {
        latestByHive[insp.hive_id] = insp;
      } else if (currentDate.getTime() === existingDate.getTime()) {
        // Same date — use created_at as tiebreaker
        const existingCreated = new Date(existing.created_at || existing.date);
        const currentCreated = new Date(insp.created_at || insp.date);
        if (currentCreated > existingCreated) {
          latestByHive[insp.hive_id] = insp;
        }
      }
    }
  });
  Object.values(latestByHive).forEach(insp => latestInspectionIds.add(insp.id));
  const isLatestInspection = (insp) => latestInspectionIds.has(insp.id);

  const filteredYards = selectedYard === 'all' ? yards : yards.filter(y => y.name === selectedYard);
  
  // Filter honey harvest data based on selected yard
  const filteredHoneyHarvestData = selectedYard === 'all' ? honeyHarvestData : 
    Object.fromEntries(Object.entries(honeyHarvestData).filter(([yard]) => yard === selectedYard));
  
  // Filter health data - need to recalculate from hivesByHealth based on selected yard
  const getFilteredHealthData = () => {
    if (selectedYard === 'all') return healthData;
    
    // Recalculate health counts from filtered hives
    const filteredHealthy = hivesByHealth.healthy.filter(h => h.yard === selectedYard);
    const filteredWarning = hivesByHealth.warning.filter(h => h.yard === selectedYard);
    const filteredCritical = hivesByHealth.critical.filter(h => h.yard === selectedYard);
    const filteredUnknown = hivesByHealth.unknown.filter(h => h.yard === selectedYard);
    
    return [
      { name: 'Healthy', value: filteredHealthy.length, color: '#22c55e', hives: filteredHealthy },
      { name: 'Warning', value: filteredWarning.length, color: '#f59e0b', hives: filteredWarning },
      { name: 'Critical', value: filteredCritical.length, color: '#ef4444', hives: filteredCritical },
      { name: 'Unknown', value: filteredUnknown.length, color: '#9ca3af', hives: filteredUnknown },
    ].filter(d => d.value > 0);
  };
  const filteredHealthData = getFilteredHealthData();
  
  // Filter hives by health for selected yard
  const getFilteredHivesByHealth = () => {
    if (selectedYard === 'all') return hivesByHealth;
    return {
      healthy: hivesByHealth.healthy.filter(h => h.yard === selectedYard),
      warning: hivesByHealth.warning.filter(h => h.yard === selectedYard),
      critical: hivesByHealth.critical.filter(h => h.yard === selectedYard),
      unknown: hivesByHealth.unknown.filter(h => h.yard === selectedYard),
    };
  };
  const filteredHivesByHealth = getFilteredHivesByHealth();
  
  // Filter fed hives
  const filteredFedHives = selectedYard === 'all' ? fedHives : fedHives.filter(h => h.yard === selectedYard);
  
  // Calculate filtered stats
  const filteredHivesBeingFed = filteredFedHives.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-stone-600 text-lg">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="p-4">
        <div className="max-w-7xl flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-amber-600" />
              Analytics Dashboard
            </h1>
            <p className="text-sm text-stone-500 mt-1">Insights & reports for your operation</p>
          </div>
          
          {/* Yard Filter Dropdown + Tour */}
          <div className="flex items-center gap-2">
            <PageTourButton pageView="analytics" />
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-1 border border-stone-200 shadow-sm">
            <Filter className="h-4 w-4 text-amber-600" />
            <Select value={selectedYard} onValueChange={setSelectedYard}>
              <SelectTrigger data-testid="analytics-yard-filter-select" className="w-[180px] h-9 bg-transparent border-0 text-stone-800 font-medium focus:ring-0">
                <SelectValue placeholder="Filter by Apiary" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Apiaries</SelectItem>
                {yards.map((yard) => (
                  <SelectItem key={yard.id} value={yard.name}>
                    {yard.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={exportToCSV}
              className="h-10 px-4 font-medium bg-white hover:bg-stone-100 text-amber-700 border border-stone-200 shadow-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="secondary"
              onClick={exportToPDF}
              disabled={exporting}
              className="h-10 px-4 font-medium bg-white hover:bg-stone-100 text-teal-700 border-0"
            >
              <FileText className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </div>
        </div>
      </div>

      <div ref={analyticsRef} className="max-w-7xl p-4 md:p-8 space-y-6">
        {/* Selected Yard Indicator */}
        {selectedYard !== 'all' && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-2 flex items-center gap-2">
            <span className="text-amber-800 font-semibold">📍 Showing data for:</span>
            <span className="bg-amber-600 text-white px-3 py-1 rounded-full font-bold">{selectedYard}</span>
          </div>
        )}
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            title="Active Hives"
            value={filteredHives.filter(h => h.status === 'active').length}
            icon={<Activity className="h-8 w-8 text-amber-600" />}
            color="amber"
          />
          <StatCard
            title="Ready Hives"
            value={filteredHives.filter(h => h.status === 'ready').length}
            icon={<Package className="h-8 w-8 text-sky-600" />}
            color="sky"
          />
          <StatCard
            title={selectedYard === 'all' ? "Total Apiaries" : "Apiary"}
            value={selectedYard === 'all' ? Object.keys(yardStats).length : 1}
            icon={<MapPin className="h-8 w-8 text-blue-600" />}
            color="blue"
          />
          <StatCard
            title="Hives Being Fed"
            value={filteredHivesBeingFed}
            icon={<Droplets className="h-8 w-8 text-green-600" />}
            color="green"
            onClick={() => setShowFedHivesModal(true)}
            clickable
          />
          <StatCard
            title="Total Inspections"
            value={filteredInspections.length}
            icon={<Activity className="h-8 w-8 text-lime-600" />}
            color="lime"
          />
        </div>

        {/* Fed Hives Modal - uses filtered fed hives */}
        {showFedHivesModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowFedHivesModal(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-stone-800 mb-4">🍯 Hives Being Fed ({filteredFedHives.length})</h3>
              {filteredFedHives.length === 0 ? (
                <p className="text-stone-500">No hives are currently being fed{selectedYard !== 'all' ? ` in ${selectedYard}` : ''}.</p>
              ) : (
                <div className="space-y-2">
                  {filteredFedHives.map(hive => (
                    <button
                      key={hive.id}
                      onClick={() => { setShowFedHivesModal(false); navigate(`/?view=hive-detail&hiveId=${hive.id}`); }}
                      className="w-full text-left bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl p-3 transition-colors"
                    >
                      <div className="font-bold text-stone-800">Hive {hive.hive_number}</div>
                      <div className="text-sm text-stone-600">{hive.yard}</div>
                      <div className="text-xs text-green-600 mt-1">Feed: {hive.feedType}</div>
                    </button>
                  ))}
                </div>
              )}
              <Button onClick={() => setShowFedHivesModal(false)} className="w-full mt-4">Close</Button>
            </div>
          </div>
        )}

        {/* Due for Inspection Modal */}
        {showDueInspectionModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDueInspectionModal(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-stone-800 mb-4">🔍 Hives Due for Inspection</h3>
              {(() => {
                const dueHives = filteredHives.filter(h => {
                  if (h.status === 'ready' || h.status === 'inactive' || h.status === 'destroyed') return false;
                  if (!h.last_inspection) return true;
                  const days = Math.floor((new Date() - new Date(h.last_inspection)) / (1000*60*60*24));
                  return days > 14;
                });
                if (dueHives.length === 0) return <p className="text-stone-500">No hives due for inspection{selectedYard !== 'all' ? ` in ${selectedYard}` : ''}.</p>;
                return (
                  <div className="space-y-2">
                    {dueHives.map(hive => {
                      const daysSince = hive.last_inspection 
                        ? Math.floor((new Date() - new Date(hive.last_inspection)) / (1000*60*60*24))
                        : null;
                      return (
                        <button
                          key={hive.id}
                          onClick={() => { setShowDueInspectionModal(false); navigate(`/?view=hive-detail&hiveId=${hive.id}`); }}
                          className="w-full text-left bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-xl p-3 transition-colors"
                        >
                          <div className="font-bold text-stone-800">Hive {hive.hive_number}</div>
                          <div className="text-sm text-stone-600">{hive.yard}</div>
                          <div className="text-xs text-orange-600 mt-1">
                            {daysSince ? `${daysSince} days since last inspection` : 'Never inspected'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
              <Button onClick={() => setShowDueInspectionModal(false)} className="w-full mt-4">Close</Button>
            </div>
          </div>
        )}

        {/* Queen Status Modal */}
        {showQueenStatusModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowQueenStatusModal(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-stone-800 mb-4">
                👑 {showQueenStatusModal === 'spotted' ? 'Queen Spotted' : 
                    showQueenStatusModal === 'noQueen' ? 'No Queen' :
                    showQueenStatusModal === 'cells' ? 'Queen Cells' : 'Drone Layer'}
              </h3>
              {(() => {
                const matchingInspections = filteredInspections.filter(insp => {
                  if (!isLatestInspection(insp)) return false;
                  if (showQueenStatusModal === 'spotted') return insp.queen_spotted;
                  if (showQueenStatusModal === 'noQueen') return insp.no_queen;
                  if (showQueenStatusModal === 'cells') return insp.queen_cells;
                  if (showQueenStatusModal === 'droneLayer') return insp.drone_layer;
                  return false;
                });
                
                if (matchingInspections.length === 0) return <p className="text-stone-500">No hives with this status{selectedYard !== 'all' ? ` in ${selectedYard}` : ''}.</p>;
                
                return (
                  <div className="space-y-2">
                    {matchingInspections.map(insp => {
                      const hive = hives.find(h => h.id === insp.hive_id);
                      if (!hive) return null;
                      return (
                        <button
                          key={insp.id}
                          onClick={() => { setShowQueenStatusModal(null); navigate(`/?view=hive-detail&hiveId=${hive.id}`); }}
                          className={`w-full text-left rounded-xl p-3 transition-colors border ${
                            showQueenStatusModal === 'spotted' ? 'bg-lime-50 hover:bg-lime-100 border-lime-200' :
                            showQueenStatusModal === 'noQueen' ? 'bg-red-50 hover:bg-red-100 border-red-200' :
                            showQueenStatusModal === 'cells' ? 'bg-amber-50 hover:bg-amber-100 border-amber-200' :
                            'bg-orange-50 hover:bg-orange-100 border-orange-200'
                          }`}
                        >
                          <div className="font-bold text-stone-800">Hive {hive.hive_number}</div>
                          <div className="text-sm text-stone-600">{hive.yard}</div>
                          <div className="text-xs text-stone-500 mt-1">Inspection: {new Date(insp.date).toLocaleDateString()}</div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
              <Button onClick={() => setShowQueenStatusModal(null)} className="w-full mt-4">Close</Button>
            </div>
          </div>
        )}

        {/* Disease & Pest Modal */}
        {showDiseasePestModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDiseasePestModal(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-stone-800 mb-4">
                {showDiseasePestModal === 'diseases' ? '🦠 Hives with Diseases' : '🐛 Hives with Pests'}
              </h3>
              {(() => {
                const affectedHives = [];
                filteredInspections.forEach(insp => {
                  const items = showDiseasePestModal === 'diseases' ? (insp.diseases || []) : (insp.pests || []);
                  if (items.length > 0) {
                    const hive = hives.find(h => h.id === insp.hive_id);
                    if (hive && !affectedHives.find(ah => ah.hive.id === hive.id)) {
                      affectedHives.push({ hive, items, date: insp.date });
                    }
                  }
                });
                
                if (affectedHives.length === 0) {
                  return <p className="text-stone-500">
                    No {showDiseasePestModal === 'diseases' ? 'diseases' : 'pests'} detected{selectedYard !== 'all' ? ` in ${selectedYard}` : ''}.
                  </p>;
                }
                
                return (
                  <div className="space-y-2">
                    {affectedHives.map(({ hive, items, date }) => (
                      <button
                        key={hive.id}
                        onClick={() => { setShowDiseasePestModal(null); navigate(`/?view=hive-detail&hiveId=${hive.id}`); }}
                        className={`w-full text-left rounded-xl p-3 transition-colors border ${
                          showDiseasePestModal === 'diseases' ? 'bg-red-50 hover:bg-red-100 border-red-200' : 'bg-orange-50 hover:bg-orange-100 border-orange-200'
                        }`}
                      >
                        <div className="font-bold text-stone-800">Hive {hive.hive_number}</div>
                        <div className="text-sm text-stone-600">{hive.yard}</div>
                        <div className={`text-xs mt-1 ${showDiseasePestModal === 'diseases' ? 'text-red-600' : 'text-orange-600'}`}>
                          {items.join(', ')}
                        </div>
                        <div className="text-xs text-stone-400 mt-1">Reported: {new Date(date).toLocaleDateString()}</div>
                      </button>
                    ))}
                  </div>
                );
              })()}
              <Button onClick={() => setShowDiseasePestModal(null)} className="w-full mt-4">Close</Button>
            </div>
          </div>
        )}

        {/* Mite Load Modal */}
        {showMiteLoadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowMiteLoadModal(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-stone-800 mb-4">
                🔬 {showMiteLoadModal === 'low' ? 'Low Mite Load (<2%)' : 
                    showMiteLoadModal === 'moderate' ? 'Moderate Mite Load (2-4%)' : 'High Mite Load (>4%)'}
              </h3>
              {(() => {
                const matchingInspections = filteredInspections.filter(i => {
                  if (!i.mite_percentage) return false;
                  const miteVal = parseFloat(i.mite_percentage);
                  if (showMiteLoadModal === 'low') return miteVal < 2;
                  if (showMiteLoadModal === 'moderate') return miteVal >= 2 && miteVal < 4;
                  if (showMiteLoadModal === 'high') return miteVal >= 4;
                  return false;
                });
                
                if (matchingInspections.length === 0) {
                  return <p className="text-stone-500">
                    No inspections with {showMiteLoadModal} mite load{selectedYard !== 'all' ? ` in ${selectedYard}` : ''}.
                  </p>;
                }
                
                // Group by hive, show latest inspection per hive
                const hiveMap = {};
                matchingInspections.forEach(insp => {
                  if (!hiveMap[insp.hive_id] || new Date(insp.date) > new Date(hiveMap[insp.hive_id].date)) {
                    hiveMap[insp.hive_id] = insp;
                  }
                });
                
                return (
                  <div className="space-y-2">
                    {Object.values(hiveMap).map(insp => {
                      const hive = hives.find(h => h.id === insp.hive_id);
                      if (!hive) return null;
                      return (
                        <button
                          key={insp.id}
                          onClick={() => { setShowMiteLoadModal(null); navigate(`/?view=hive-detail&hiveId=${hive.id}`); }}
                          className={`w-full text-left rounded-xl p-3 transition-colors border ${
                            showMiteLoadModal === 'low' ? 'bg-green-50 hover:bg-green-100 border-green-200' :
                            showMiteLoadModal === 'moderate' ? 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200' :
                            'bg-red-50 hover:bg-red-100 border-red-200'
                          }`}
                        >
                          <div className="font-bold text-stone-800">Hive {hive.hive_number}</div>
                          <div className="text-sm text-stone-600">{hive.yard}</div>
                          <div className={`text-xs mt-1 font-bold ${
                            showMiteLoadModal === 'low' ? 'text-green-600' :
                            showMiteLoadModal === 'moderate' ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            Mite Load: {insp.mite_percentage}%
                          </div>
                          <div className="text-xs text-stone-400 mt-1">Tested: {new Date(insp.date).toLocaleDateString()}</div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
              <Button onClick={() => setShowMiteLoadModal(null)} className="w-full mt-4">Close</Button>
            </div>
          </div>
        )}

        {/* Field Reports - Full Section - uses filtered data */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-stone-800 font-heading mb-4 flex items-center gap-2">
            📋 Field Reports {selectedYard !== 'all' && <span className="text-sm font-normal text-stone-500">- {selectedYard}</span>}
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div 
              className="bg-orange-50 border border-orange-200 rounded-xl p-4 cursor-pointer hover:bg-orange-100 transition-colors"
              onClick={() => setShowDueInspectionModal(true)}
            >
              <h3 className="font-bold text-orange-800 mb-2">🔍 Due for Inspection</h3>
              <div className="text-3xl font-extrabold text-orange-600">
                {filteredHives.filter(h => {
                  if (h.status === 'ready' || h.status === 'inactive' || h.status === 'destroyed') return false;
                  if (!h.last_inspection) return true;
                  const days = Math.floor((new Date() - new Date(h.last_inspection)) / (1000*60*60*24));
                  return days > 14;
                }).length}
              </div>
              <p className="text-sm text-orange-700 mt-1">hives not inspected in 14+ days</p>
              <p className="text-xs text-orange-500 mt-2">Click to view hives</p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h3 className="font-bold text-green-800 mb-2">✅ Active Hives</h3>
              <div className="text-3xl font-extrabold text-green-600">
                {filteredHives.filter(h => h.status === 'active').length}
              </div>
              <p className="text-sm text-green-700 mt-1">hives in active management</p>
            </div>

            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
              <h3 className="font-bold text-sky-800 mb-2">📦 Ready Hives</h3>
              <div className="text-3xl font-extrabold text-sky-600">
                {filteredHives.filter(h => h.status === 'ready').length}
              </div>
              <p className="text-sm text-sky-700 mt-1">empty equipment awaiting bees</p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="font-bold text-red-800 mb-2">🚫 Inactive Hives</h3>
              <div className="text-3xl font-extrabold text-red-600">
                {filteredHives.filter(h => h.status === 'inactive').length}
              </div>
              <p className="text-sm text-red-700 mt-1">hives marked inactive</p>
            </div>
          </div>
          
          {/* Queen Status - uses filtered inspections */}
          <h3 className="text-lg font-bold text-stone-800 mt-8 mb-4 flex items-center gap-2">
            👑 Queen Status (from latest inspections)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div 
              className="bg-lime-50 border border-lime-200 rounded-lg p-3 text-center cursor-pointer hover:bg-lime-100 transition-colors"
              onClick={() => setShowQueenStatusModal('spotted')}
            >
              <div className="text-2xl font-bold text-lime-600">
                {filteredInspections.filter(insp => isLatestInspection(insp) && insp.queen_spotted).length}
              </div>
              <div className="text-xs text-lime-700">Queen Spotted</div>
            </div>
            <div 
              className="bg-red-50 border border-red-200 rounded-lg p-3 text-center cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => setShowQueenStatusModal('noQueen')}
            >
              <div className="text-2xl font-bold text-red-600">
                {filteredInspections.filter(insp => isLatestInspection(insp) && insp.no_queen).length}
              </div>
              <div className="text-xs text-red-700">No Queen</div>
            </div>
            <div 
              className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => setShowQueenStatusModal('cells')}
            >
              <div className="text-2xl font-bold text-amber-600">
                {filteredInspections.filter(insp => isLatestInspection(insp) && insp.queen_cells).length}
              </div>
              <div className="text-xs text-amber-700">Queen Cells</div>
            </div>
            <div 
              className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center cursor-pointer hover:bg-orange-100 transition-colors"
              onClick={() => setShowQueenStatusModal('droneLayer')}
            >
              <div className="text-2xl font-bold text-orange-600">
                {filteredInspections.filter(insp => isLatestInspection(insp) && insp.drone_layer).length}
              </div>
              <div className="text-xs text-orange-700">Drone Layer</div>
            </div>
          </div>
          
          {/* Disease & Pest Overview - uses filtered inspections */}
          <h3 className="text-lg font-bold text-stone-800 mt-8 mb-4 flex items-center gap-2">
            🦠 Disease & Pest Alerts
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div 
              className="bg-red-50 border border-red-200 rounded-lg p-4 cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => setShowDiseasePestModal('diseases')}
            >
              <h4 className="font-bold text-red-800 mb-2">Diseases Detected</h4>
              {(() => {
                const diseaseCount = {};
                filteredInspections.forEach(insp => {
                  (insp.diseases || []).forEach(d => {
                    diseaseCount[d] = (diseaseCount[d] || 0) + 1;
                  });
                });
                const entries = Object.entries(diseaseCount);
                if (entries.length === 0) return <p className="text-sm text-red-600">No diseases reported</p>;
                return (
                  <div className="space-y-1">
                    {entries.map(([disease, count]) => (
                      <div key={disease} className="flex justify-between text-sm">
                        <span className="text-red-700">{disease}</span>
                        <span className="font-bold text-red-600">{count} reports</span>
                      </div>
                    ))}
                    <p className="text-xs text-red-500 mt-2">Click to view affected hives</p>
                  </div>
                );
              })()}
            </div>
            <div 
              className="bg-orange-50 border border-orange-200 rounded-lg p-4 cursor-pointer hover:bg-orange-100 transition-colors"
              onClick={() => setShowDiseasePestModal('pests')}
            >
              <h4 className="font-bold text-orange-800 mb-2">Pests Detected</h4>
              {(() => {
                const pestCount = {};
                filteredInspections.forEach(insp => {
                  (insp.pests || []).forEach(p => {
                    pestCount[p] = (pestCount[p] || 0) + 1;
                  });
                });
                const entries = Object.entries(pestCount);
                if (entries.length === 0) return <p className="text-sm text-orange-600">No pests reported</p>;
                return (
                  <div className="space-y-1">
                    {entries.map(([pest, count]) => (
                      <div key={pest} className="flex justify-between text-sm">
                        <span className="text-orange-700">{pest}</span>
                        <span className="font-bold text-orange-600">{count} reports</span>
                      </div>
                    ))}
                    <p className="text-xs text-orange-500 mt-2">Click to view affected hives</p>
                  </div>
                );
              })()}
            </div>
          </div>
          
          {/* Mite Control Section - Enhanced */}
          <h3 className="text-lg font-bold text-stone-800 mt-8 mb-4 flex items-center gap-2">
            🔬 Mite Control Overview
          </h3>
          
          {/* Mite Load Cards */}
          <p className="text-sm text-stone-600 mb-2">Mite Load Distribution (from inspections)</p>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div 
              className="bg-green-50 border border-green-200 rounded-lg p-3 text-center cursor-pointer hover:bg-green-100 transition-colors"
              onClick={() => setShowMiteLoadModal('low')}
            >
              <div className="text-2xl font-bold text-green-600">
                {filteredInspections.filter(i => i.mite_percentage && parseFloat(i.mite_percentage) < 2).length}
              </div>
              <div className="text-xs text-green-700">Low (&lt;2%)</div>
            </div>
            <div 
              className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center cursor-pointer hover:bg-yellow-100 transition-colors"
              onClick={() => setShowMiteLoadModal('moderate')}
            >
              <div className="text-2xl font-bold text-yellow-600">
                {filteredInspections.filter(i => i.mite_percentage && parseFloat(i.mite_percentage) >= 2 && parseFloat(i.mite_percentage) < 4).length}
              </div>
              <div className="text-xs text-yellow-700">Moderate (2-4%)</div>
            </div>
            <div 
              className="bg-red-50 border border-red-200 rounded-lg p-3 text-center cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => setShowMiteLoadModal('high')}
            >
              <div className="text-2xl font-bold text-red-600">
                {filteredInspections.filter(i => i.mite_percentage && parseFloat(i.mite_percentage) >= 4).length}
              </div>
              <div className="text-xs text-red-700">High (&gt;4%)</div>
            </div>
          </div>
          
          {/* Treatment Summary */}
          {Object.keys(treatmentData).length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h4 className="font-bold text-purple-800 mb-2">💊 Treatments Applied</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(treatmentData).map(([treatment, data]) => (
                  <div key={treatment} className="bg-white rounded-lg p-2 border border-purple-100">
                    <div className="text-sm font-medium text-purple-700">{formatTreatmentName(treatment)}</div>
                    <div className="text-xs text-stone-500">
                      {data.count} applications • {data.hives.length} hives
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Mite Count Records */}
          {miteCountRecords.length > 0 && (
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
              <h4 className="font-bold text-stone-800 mb-2">📊 Recent Mite Counts</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {miteCountRecords.slice(0, 10).map((record, idx) => {
                  const hive = hives.find(h => h.id === record.hive_id);
                  const percentage = record.percentage || ((record.mite_count / (record.bee_count || 300)) * 100);
                  const level = percentage < 2 ? 'green' : percentage < 4 ? 'yellow' : 'red';
                  return (
                    <div key={idx} className="flex justify-between items-center text-sm bg-white p-2 rounded border">
                      <span className="text-stone-700">
                        {hive ? `Hive ${hive.hive_number}` : 'Unknown Hive'}
                        <span className="text-stone-400 ml-2 text-xs">
                          {new Date(record.date).toLocaleDateString()}
                        </span>
                      </span>
                      <span className={`font-bold text-${level}-600`}>
                        {percentage.toFixed(1)}% ({record.mite_count}/{record.bee_count || 300})
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Treatment Plan History */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-4">
            <h4 className="font-bold text-indigo-800 mb-3">📋 Treatment Plan History</h4>
            {treatmentPlans.length > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-white rounded-lg p-3 border border-indigo-100 text-center">
                  <div className="text-2xl font-bold text-indigo-600">{treatmentPlans.length}</div>
                  <div className="text-xs text-stone-500">Total Plans</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-green-100 text-center">
                  <div className="text-2xl font-bold text-green-600">{treatmentPlans.filter(p => p.status === 'completed').length}</div>
                  <div className="text-xs text-stone-500">Completed</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
                  <div className="text-2xl font-bold text-blue-600">{treatmentPlans.filter(p => p.status === 'active').length}</div>
                  <div className="text-xs text-stone-500">Active</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                  <div className="text-2xl font-bold text-amber-600">{treatmentPlans.filter(p => p.status === 'scheduled').length}</div>
                  <div className="text-xs text-stone-500">Scheduled</div>
                </div>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {treatmentPlans
                  .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''))
                  .map(plan => {
                    const totalTasks = (plan.tasks || []).length;
                    const doneTasks = (plan.tasks || []).filter(t => t.status === 'completed').length;
                    const hiveCount = (plan.hive_ids || []).length;
                    return (
                      <div key={plan.id} className="bg-white rounded-lg p-3 border border-indigo-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-stone-800 text-sm">{formatTreatmentName(plan.name || plan.treatment_type_name || plan.treatment_type) || 'Treatment Plan'}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              plan.status === 'completed' ? 'bg-green-100 text-green-700' :
                              plan.status === 'active' ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>{plan.status}</span>
                          </div>
                          <span className="text-xs text-stone-400">
                            {plan.start_date ? new Date(plan.start_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 text-xs text-stone-500 flex-wrap">
                          <span>{formatTreatmentName(plan.treatment_type_name || plan.treatment_type || plan.mite_type) || 'Treatment'}</span>
                          <span>{hiveCount} hive{hiveCount !== 1 ? 's' : ''}</span>
                          {totalTasks > 0 && (
                            <span className="flex items-center gap-1">
                              {doneTasks}/{totalTasks} tasks done
                              <span className="inline-block w-16 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                                <span className="block h-full bg-green-500 rounded-full" style={{ width: `${(doneTasks / totalTasks) * 100}%` }} />
                              </span>
                            </span>
                          )}
                          {plan.post_treatment_mite_count && (
                            <span className="text-green-600">Post: {plan.post_treatment_mite_count}%</span>
                          )}
                          {plan.pre_treatment_mite_count && (
                            <span className="text-red-500">Pre: {plan.pre_treatment_mite_count}%</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
              </>
            ) : (
              <p className="text-sm text-stone-500 py-3">No treatment plans created yet. Go to Mite Control to create your first plan.</p>
            )}
          </div>
          
          {/* Honey Harvest Summary - Using harvest feature data (matches Dashboard) */}
          <h3 className="text-lg font-bold text-stone-800 mt-8 mb-4 flex items-center gap-2">
            🍯 Harvest Summary
          </h3>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            {harvestSummary ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                    <div className="text-2xl font-bold text-amber-600">
                      {Math.round(kgTo(harvestSummary.current_season?.total_kg || 0, displayUnit(harvestSummary.default_unit)) * 10) / 10} {displayUnit(harvestSummary.default_unit)}
                    </div>
                    <div className="text-xs text-stone-500">This Season</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {harvestSummary.current_season?.harvest_count || 0}
                    </div>
                    <div className="text-xs text-stone-500">Harvests This Season</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {harvestSummary.current_season?.total_frames || 0}
                    </div>
                    <div className="text-xs text-stone-500">Frames This Season</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                    <div className="text-2xl font-bold text-stone-600">
                      {Math.round(kgTo(harvestSummary.all_time?.total_kg || 0, displayUnit(harvestSummary.default_unit)) * 10) / 10} {displayUnit(harvestSummary.default_unit)}
                    </div>
                    <div className="text-xs text-stone-500">All Time Total</div>
                  </div>
                </div>
                
                {/* All Time Stats */}
                <div className="bg-white rounded-lg p-3 border border-amber-100">
                  <h4 className="font-medium text-amber-800 mb-2">📊 All Time Statistics</h4>
                  <div className="grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="font-bold text-stone-700">{harvestSummary.all_time?.harvest_count || 0}</div>
                      <div className="text-stone-500 text-xs">Total Harvests</div>
                    </div>
                    <div>
                      <div className="font-bold text-stone-700">{harvestSummary.all_time?.total_frames || 0}</div>
                      <div className="text-stone-500 text-xs">Total Frames</div>
                    </div>
                    <div>
                      <div className="font-bold text-stone-700">{harvestSummary.years?.length || 0}</div>
                      <div className="text-stone-500 text-xs">Seasons Tracked</div>
                    </div>
                  </div>
                </div>
                
                {harvestSummary.current_year && (
                  <p className="text-xs text-stone-500 mt-2 text-center">
                    Current harvest year: {harvestSummary.current_year}
                  </p>
                )}
              </>
            ) : (
              <p className="text-center text-stone-500 italic py-4">No harvest data available. Start logging harvests from the Dashboard!</p>
            )}
          </div>
          
          {/* Weight Analytics Section - Below Harvest Summary */}
          <h3 className="text-lg font-bold text-stone-800 mt-8 mb-4 flex items-center gap-2">
            <Scale className="h-5 w-5 text-emerald-600" />
            ⚖️ Hive Weight Analytics
          </h3>
          
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            {weightAnalytics && weightAnalytics.total_hives_with_weight > 0 ? (
              <>
                {/* Weight Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                    <div className="text-2xl font-bold text-emerald-600">
                      {weightAnalytics.total_hives_with_weight}
                    </div>
                    <div className="text-xs text-stone-500">Hives with Data</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {weightAnalytics.unit_preference === 'kg' 
                        ? `${weightAnalytics.average_weight_kg}kg`
                        : `${weightAnalytics.average_weight_lbs}lbs`
                      }
                    </div>
                    <div className="text-xs text-stone-500">Average Weight</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                    <div className={`text-2xl font-bold flex items-center justify-center gap-1 ${
                      weightAnalytics.average_change_kg >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {weightAnalytics.average_change_kg >= 0 
                        ? <TrendingUp className="h-5 w-5" />
                        : <TrendingDown className="h-5 w-5" />
                      }
                      {weightAnalytics.unit_preference === 'kg' 
                        ? `${weightAnalytics.average_change_kg}kg`
                        : `${weightAnalytics.average_change_lbs}lbs`
                      }
                    </div>
                    <div className="text-xs text-stone-500">Average Change</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                    <div className="text-2xl font-bold text-amber-600 uppercase">
                      {weightAnalytics.unit_preference}
                    </div>
                    <div className="text-xs text-stone-500">Unit</div>
                  </div>
                </div>
                
                {/* Top Gainers and Losers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Top Gainers */}
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" /> Top Weight Gainers
                    </h4>
                    {weightAnalytics.top_gainers && weightAnalytics.top_gainers.filter(h => h.weight_change_kg > 0).length > 0 ? (
                      <div className="space-y-1">
                        {weightAnalytics.top_gainers.filter(h => h.weight_change_kg > 0).slice(0, 3).map((hive) => (
                          <div key={hive.hive_id} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                            <span className="font-medium text-stone-700">{hive.hive_name || `Hive ${hive.hive_id.slice(0,8)}`}</span>
                            <span className="text-green-600 font-bold">
                              +{weightAnalytics.unit_preference === 'kg' 
                                ? `${hive.weight_change_kg}kg`
                                : `${hive.weight_change_lbs}lbs`
                              }
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No weight gains recorded yet</p>
                    )}
                  </div>
                  
                  {/* Hives Losing Weight */}
                  <div className="bg-white rounded-lg p-3 border border-red-200">
                    <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                      <TrendingDown className="h-4 w-4" /> Hives Losing Weight
                    </h4>
                    {weightAnalytics.top_losers && weightAnalytics.top_losers.filter(h => h.weight_change_kg < 0).length > 0 ? (
                      <div className="space-y-1">
                        {weightAnalytics.top_losers.filter(h => h.weight_change_kg < 0).slice(0, 3).map((hive) => (
                          <div key={hive.hive_id} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                            <span className="font-medium text-stone-700">{hive.hive_name || `Hive ${hive.hive_id.slice(0,8)}`}</span>
                            <span className="text-red-600 font-bold">
                              {weightAnalytics.unit_preference === 'kg' 
                                ? `${hive.weight_change_kg}kg`
                                : `${hive.weight_change_lbs}lbs`
                              }
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No weight losses recorded</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-center text-stone-500 italic py-4">
                No weight data available. Record hive weights during inspections to track colony health!
              </p>
            )}
          </div>
        </div>

        {harvests.length > 0 && (() => {
          const seasons = [...new Set(harvests.map(h => h.harvest_year).filter(Boolean))].sort().reverse();
          const season = yieldSeason || harvestSummary?.current_year || seasons[0] || 'all';
          const list = season === 'all' ? harvests : harvests.filter(h => h.harvest_year === season);
          return (
            <ApiaryYieldComparison
              testId="analytics-apiary-yield"
              harvests={list}
              hives={hives}
              unit={displayUnit(harvestSummary?.default_unit)}
              subtitle={season === 'all' ? 'All seasons' : `Season ${season}`}
              headerRight={(
                <Select value={season} onValueChange={setYieldSeason}>
                  <SelectTrigger data-testid="analytics-yield-season-select" className="w-36 h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All seasons</SelectItem>
                    {[...new Set([...seasons, season].filter(v => v && v !== 'all'))].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            />
          );
        })()}

        {/* Charts: Harvest Overview and Hive Health Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Harvest Overview - shows harvests by yard */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-stone-800 font-heading mb-4 flex items-center gap-2">
              <Droplets className="h-6 w-6 text-amber-600" />
              Harvest Overview
            </h2>
            <p className="text-sm text-stone-500 mb-4">Total harvests by apiary</p>
            {(() => {
              // Build harvest by yard data from harvests list
              if (!harvests || harvests.length === 0) {
                return (
                  <div className="h-[300px] flex flex-col items-center justify-center text-stone-500">
                    <Droplets className="h-12 w-12 text-stone-300 mb-3" />
                    <p>No harvest data available</p>
                    <p className="text-sm mt-1">Start logging harvests from the Dashboard!</p>
                  </div>
                );
              }
              
              // Group harvests by yard
              const harvestByYard = {};
              harvests.forEach(h => {
                const yard = harvestYard(h, hives);
                if (!harvestByYard[yard]) {
                  harvestByYard[yard] = { totalKg: 0, count: 0, frames: 0 };
                }
                harvestByYard[yard].totalKg += toUnit(h, displayUnit(harvestSummary?.default_unit));
                harvestByYard[yard].count += 1;
                harvestByYard[yard].frames += h.frames_harvested || 0;
              });
              
              const harvestChartData = Object.entries(harvestByYard).map(([yard, data]) => ({
                yard: yard,
                'Total KG': parseFloat(data.totalKg.toFixed(1)),
                'Harvests': data.count,
                'Frames': data.frames
              }));
              
              // Filter by selected yard
              const filteredHarvestData = selectedYard === 'all' 
                ? harvestChartData 
                : harvestChartData.filter(d => d.yard === selectedYard);
              
              if (filteredHarvestData.length === 0) {
                return (
                  <div className="h-[300px] flex items-center justify-center text-stone-500">
                    No harvest data for {selectedYard !== 'all' ? selectedYard : 'any apiary'}
                  </div>
                );
              }
              
              return (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={filteredHarvestData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="yard" type="category" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white border border-stone-200 rounded-lg p-3 shadow-lg">
                              <p className="font-bold text-stone-800">{data.yard}</p>
                              <p className="text-amber-600">{data['Total KG']} {displayUnit(harvestSummary?.default_unit)} harvested</p>
                              <p className="text-stone-600">{data['Harvests']} harvests</p>
                              <p className="text-stone-500 text-sm">{data['Frames']} frames</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="Total KG" fill="#D97706" name={`Honey (${displayUnit(harvestSummary?.default_unit)})`} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </div>

          {/* Hive Health Overview - fixed click behavior */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold text-stone-800 font-heading mb-4 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              Hive Health Overview
            </h2>
            <p className="text-sm text-stone-500 mb-4">Distribution of hive health status</p>
            {filteredHealthData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={filteredHealthData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {filteredHealthData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={({ payload }) => {
                    if (payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white border border-stone-200 rounded-lg p-3 shadow-lg">
                          <div className="font-bold" style={{ color: data.color }}>{data.name}</div>
                          <div className="text-stone-700">{data.value} hives</div>
                          {data.hives && data.hives.length > 0 && (
                            <div className="text-xs text-stone-500 mt-1">
                              {data.hives.slice(0, 3).map(h => `Hive ${h.hive_number}`).join(', ')}
                              {data.hives.length > 3 && ` +${data.hives.length - 3} more`}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex flex-col items-center justify-center text-stone-500">
                <AlertTriangle className="h-12 w-12 text-stone-300 mb-3" />
                <p>No health data available</p>
                <p className="text-sm mt-1">Complete inspections to see health status</p>
              </div>
            )}
          </div>
        </div>

        {/* Split Results - success rate per split method */}
        <SplitResultsCard />

        {/* Hives by Apiary - uses filtered yards */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-stone-800 font-heading mb-4 flex items-center gap-2">
            <MapPin className="h-6 w-6 text-blue-600" />
            Hives by Apiary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(yardStats)
              .filter(([yard]) => selectedYard === 'all' || yard === selectedYard)
              .map(([yard, data]) => (
              <div key={yard} className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                <div className="font-bold text-lg text-stone-800">{yard}</div>
                <div className="text-3xl font-extrabold text-amber-600 mt-2">{data.count}</div>
                <div className="text-sm text-stone-600 mt-1">hives</div>
              </div>
            ))}
          </div>
        </div>

        {/* Apiary Locations Map - filters by selected apiary */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-stone-800 font-heading mb-4 flex items-center gap-2">
            <MapPin className="h-6 w-6 text-red-600" />
            Apiary Locations Map
          </h2>
          <div className="h-[400px] rounded-xl overflow-hidden border-2 border-stone-200">
            {mapReady ? (
              <MapContainer
                center={mapCenter}
                zoom={selectedYard === 'all' ? 10 : 13}
                style={{ height: '100%', width: '100%' }}
                key={`map-${mapCenter[0]}-${mapCenter[1]}-${selectedYard}`}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {Object.entries(yardStats)
                  .filter(([yard]) => selectedYard === 'all' || yard === selectedYard)
                  .map(([yard, data]) => {
                  const coords = getYardCoordinates(yard);
                  if (!coords) return null;
                  const yardData = yards.find(y => y.name === yard);
                  return (
                    <Marker key={yard} position={coords}>
                      <Popup>
                        <div className="font-bold text-stone-800">{yard}</div>
                        <div className="text-stone-600">{data.count} hives</div>
                        {yardData?.location && (
                          <div className="text-xs text-stone-500 mt-1">{yardData.location}</div>
                        )}
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center bg-stone-100">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto mb-2"></div>
                  <div className="text-stone-600">Loading map locations...</div>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 text-sm text-stone-500">
            {Object.keys(geocodedYards).length > 0 
              ? `Showing ${Object.keys(geocodedYards).length} apiary location(s) based on addresses.`
              : 'Map locations are being loaded from apiary addresses.'}
          </div>
        </div>

        {/* Equipment Summary by Apiary */}
        {filteredEquipmentSummary && (
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
                📦 Equipment Summary {selectedYard !== 'all' ? `- ${selectedYard}` : 'by Apiary'}
              </h2>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={exportEquipmentCSV}
                  className="bg-lime-50 hover:bg-lime-100 text-lime-700 border border-lime-200"
                >
                  <Download className="h-4 w-4 mr-1" />
                  CSV
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={exportEquipmentPDF}
                  className="bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  PDF
                </Button>
              </div>
            </div>
            
            {/* Section 1: Hive Overview - with bordered card */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-bold text-amber-800 mb-3">🐝 Hive Overview</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-amber-200 bg-amber-100/50">
                      <th className="text-left py-3 px-3 font-bold text-stone-700 w-44 sticky left-0 bg-amber-100/50 z-10">Item</th>
                      {Object.keys(filteredEquipmentSummary.by_yard || {}).map(yard => (
                        <th key={yard} className="text-center py-3 px-3 font-bold text-amber-700 w-28 truncate" title={yard}>{yard}</th>
                      ))}
                      {selectedYard === 'all' && <th className="text-center py-3 px-3 font-bold text-stone-800 bg-stone-200 w-28 sticky right-0 z-10">Total</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-amber-100 bg-white">
                      <td className="py-2 px-3 font-medium text-stone-800 sticky left-0 bg-white z-10">Hives</td>
                      {Object.values(filteredEquipmentSummary.by_yard || {}).map((data, idx) => (
                        <td key={idx} className="py-2 px-3 text-center font-bold text-amber-600 w-28">{data.hive_count}</td>
                      ))}
                      {selectedYard === 'all' && (
                        <td className="py-2 px-3 text-center font-bold bg-stone-100 w-28 sticky right-0 z-10">
                          {Object.values(filteredEquipmentSummary.by_yard || {}).reduce((sum, d) => sum + d.hive_count, 0)}
                        </td>
                      )}
                    </tr>
                    <tr className="bg-white">
                      <td className="py-2 px-3 font-medium text-stone-800 sticky left-0 bg-white z-10">Total Frames</td>
                      {Object.values(filteredEquipmentSummary.by_yard || {}).map((data, idx) => (
                        <td key={idx} className="py-2 px-3 text-center w-28">{data.total_frames}</td>
                      ))}
                      {selectedYard === 'all' && (
                        <td className="py-2 px-3 text-center font-bold bg-stone-100 w-28 sticky right-0 z-10">
                          {Object.values(filteredEquipmentSummary.by_yard || {}).reduce((sum, d) => sum + d.total_frames, 0)}
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Hive Types Breakdown - with bordered card */}
            {(() => {
              // Collect all unique hive types across all yards
              const allHiveTypes = new Set();
              Object.values(filteredEquipmentSummary.by_yard || {}).forEach(data => {
                Object.keys(data.hive_types || {}).forEach(ht => allHiveTypes.add(ht));
              });
              
              if (allHiveTypes.size > 0) {
                return (
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-6">
                    <h4 className="text-lg font-bold text-stone-700 mb-3">📊 Hive Types by Apiary</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-stone-200 bg-stone-100/50">
                            <th className="text-left py-2 px-3 font-bold text-stone-600 w-44 sticky left-0 bg-stone-100/50 z-10">Hive Type</th>
                            {Object.keys(filteredEquipmentSummary.by_yard || {}).map(yard => (
                              <th key={yard} className="text-center py-2 px-3 font-bold text-stone-600 w-28 truncate" title={yard}>{yard}</th>
                            ))}
                            {selectedYard === 'all' && <th className="text-center py-2 px-3 font-bold text-stone-700 bg-stone-200 w-28 sticky right-0 z-10">Total</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from(allHiveTypes).map(hiveType => {
                            const total = Object.values(filteredEquipmentSummary.by_yard || {})
                              .reduce((sum, d) => sum + (d.hive_types?.[hiveType] || 0), 0);
                            
                            return (
                              <tr key={hiveType} className="border-b border-stone-100 hover:bg-stone-50 bg-white">
                                <td className="py-2 px-3 font-medium text-stone-700 capitalize sticky left-0 bg-white z-10">{hiveType.replace(/_/g, ' ')}</td>
                                {Object.values(filteredEquipmentSummary.by_yard || {}).map((data, idx) => (
                                  <td key={idx} className="py-2 px-3 text-center w-28">{data.hive_types?.[hiveType] || '-'}</td>
                                ))}
                                {selectedYard === 'all' && (
                                  <td className="py-2 px-3 text-center font-bold bg-stone-100 w-28 sticky right-0 z-10">{total}</td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Section 2: Hive Stack Configuration */}
            <h3 className="text-lg font-bold text-stone-800 mb-3">📦 Hive Stack Configuration</h3>
            
            {/* Dynamic Box Configuration Section (New) */}
            {(() => {
              // Collect all unique dynamic box names across all yards
              const allDynamicBoxes = new Set();
              Object.values(filteredEquipmentSummary.by_yard || {}).forEach(data => {
                Object.keys(data.dynamic_boxes || {}).forEach(boxName => allDynamicBoxes.add(boxName));
              });
              
              if (allDynamicBoxes.size > 0) {
                return (
                  <div className="mb-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <h4 className="text-lg font-bold text-amber-800 mb-3">📋 Dynamic Box Summary</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-amber-200 bg-amber-100/50">
                              <th className="text-left py-2 px-3 font-bold text-amber-700 w-44 sticky left-0 bg-amber-100/50 z-10">Box Type</th>
                              {Object.keys(filteredEquipmentSummary.by_yard || {}).map(yard => (
                                <th key={yard} className="text-center py-2 px-3 font-bold text-amber-700 w-28 truncate" title={yard}>{yard}</th>
                              ))}
                              {selectedYard === 'all' && <th className="text-center py-2 px-3 font-bold text-amber-900 bg-stone-200 w-28 sticky right-0 z-10">Total</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from(allDynamicBoxes).map(boxName => {
                              const totalQty = Object.values(filteredEquipmentSummary.by_yard || {})
                                .reduce((sum, d) => sum + (d.dynamic_boxes?.[boxName]?.qty || 0), 0);
                              const totalFrames = Object.values(filteredEquipmentSummary.by_yard || {})
                                .reduce((sum, d) => sum + (d.dynamic_boxes?.[boxName]?.frames || 0), 0);
                              
                              return (
                                <tr key={boxName} className="border-b border-amber-100 hover:bg-amber-50/50 bg-white">
                                  <td className="py-2 px-3 font-medium text-amber-800 sticky left-0 bg-white z-10">{boxName}</td>
                                  {Object.values(filteredEquipmentSummary.by_yard || {}).map((data, idx) => {
                                    const boxData = data.dynamic_boxes?.[boxName];
                                    if (!boxData || boxData.qty === 0) {
                                      return <td key={idx} className="py-2 px-3 text-center text-stone-400 w-28">-</td>;
                                    }
                                    return (
                                      <td key={idx} className="py-2 px-3 text-center w-28">
                                        <div className="font-semibold text-amber-800">{boxData.qty} boxes</div>
                                        <div className="text-xs text-stone-500">{boxData.frames} frames</div>
                                        {Object.keys(boxData.frame_types || {}).length > 0 && (
                                          <div className="text-xs text-blue-600 mt-1">
                                            {Object.entries(boxData.frame_types).map(([ft, count]) => `${ft}: ${count}`).join(', ')}
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}
                                  {selectedYard === 'all' && (
                                    <td className="py-2 px-3 text-center bg-stone-100 w-28 sticky right-0 z-10">
                                      <div className="font-bold text-amber-900">{totalQty}</div>
                                      <div className="text-xs text-stone-600">{totalFrames}F</div>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Legacy Stack Configuration - Only show if there are stack labels with data */}
            {filteredEquipmentSummary.stack_labels && Object.keys(filteredEquipmentSummary.stack_labels).length > 0 && 
             Object.values(filteredEquipmentSummary.by_yard || {}).some(data => 
               Object.values(data.stack || {}).some(val => val > 0)
             ) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-bold text-yellow-800 mb-3">📦 Additional Stack Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-yellow-200 bg-yellow-100/50">
                      <th className="text-left py-3 px-3 font-bold text-stone-700 w-44 sticky left-0 bg-yellow-100/50 z-10">Stack Item</th>
                      {Object.keys(filteredEquipmentSummary.by_yard || {}).map(yard => (
                        <th key={yard} className="text-center py-3 px-3 font-bold text-amber-700 w-28 truncate" title={yard}>{yard}</th>
                      ))}
                      {selectedYard === 'all' && <th className="text-center py-3 px-3 font-bold text-stone-800 bg-stone-200 w-28 sticky right-0 z-10">Total</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(filteredEquipmentSummary.stack_labels || {}).map(([key, label]) => {
                      // Only show rows that have at least one non-zero value
                      const hasData = Object.values(filteredEquipmentSummary.by_yard || {}).some(d => (d.stack?.[key] || 0) > 0);
                      if (!hasData) return null;
                      return (
                        <tr key={key} className="border-b border-yellow-100 hover:bg-yellow-50/50 bg-white">
                          <td className="py-2 px-3 font-medium text-stone-700 truncate sticky left-0 bg-white z-10 w-44" title={label}>{label}</td>
                          {Object.values(filteredEquipmentSummary.by_yard || {}).map((data, idx) => (
                            <td key={idx} className="py-2 px-3 text-center w-28">{data.stack?.[key] || 0}</td>
                          ))}
                          {selectedYard === 'all' && (
                            <td className="py-2 px-3 text-center font-bold bg-stone-100 w-28 sticky right-0 z-10">
                              {Object.values(filteredEquipmentSummary.by_yard || {}).reduce((sum, d) => sum + (d.stack?.[key] || 0), 0)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            )}
            
            {/* Apiary Tools by Category */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-bold text-green-800 mb-3">🛠️ Apiary Tools</h3>
            {(() => {
              const filteredYards = selectedYard === 'all' ? yards : yards.filter(y => y.name === selectedYard);
              
              // Define tool categories with their colors and icons
              const toolCategories = {
                'protective_gear': { label: 'Protective Gear', bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200', icon: '🧤' },
                'hive_tools': { label: 'Hive Tools', bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200', icon: '🔧' },
                'feeding_equipment': { label: 'Feeding Equipment', bg: 'bg-green-50', text: 'text-green-800', border: 'border-green-200', icon: '🍯' },
                'heavy_equipment': { label: 'Heavy Equipment', bg: 'bg-gray-50', text: 'text-gray-800', border: 'border-gray-200', icon: '🚜' },
                'miscellaneous': { label: 'Miscellaneous', bg: 'bg-cyan-50', text: 'text-cyan-800', border: 'border-cyan-200', icon: '📋' },
              };
              
              // Build tools by category entirely from toolsConfig (admin DB)
              const allTools = toolsConfig?.items || [];
              const toolsByCategory = {};
              allTools.forEach(tool => {
                const section = tool.section || 'hive_tools';
                if (!toolsByCategory[section]) toolsByCategory[section] = [];
                const formKey = tool.form_key || tool.name?.toLowerCase().replace(/\s+/g, '_') || tool.key || tool.id;
                toolsByCategory[section].push({ key: formKey, name: tool.name });
              });
              
              // Get custom categories
              const customCategories = toolsConfig?.custom_categories || [];
              
              return (
                <div className="space-y-4">
                  {/* Render each category */}
                  {Object.entries(toolCategories).map(([categoryKey, category]) => {
                    const allToolsInCategory = toolsByCategory[categoryKey] || [];
                    
                    // Only show categories with tools
                    if (allToolsInCategory.length === 0) return null;
                    
                    return (
                      <div key={categoryKey} className={`${category.bg} ${category.border} border rounded-lg p-3`}>
                        <h4 className={`font-semibold ${category.text} mb-2`}>{category.icon} {category.label}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={`border-b ${category.border}`}>
                                <th className="text-left py-2 px-2 font-medium text-stone-600 w-36">Tool</th>
                                {filteredYards.map(yard => (
                                  <th key={yard.id} className="text-center py-2 px-2 font-medium text-stone-600 w-20 truncate" title={yard.name}>{yard.name}</th>
                                ))}
                                {selectedYard === 'all' && <th className="text-center py-2 px-2 font-bold text-stone-800 bg-stone-100 w-16">Total</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {allToolsInCategory.map(({ key, name }) => {
                                const total = filteredYards.reduce((sum, y) => sum + (y.tools?.[key] || 0), 0);
                                return (
                                  <tr key={key} className="border-b border-stone-100/50 hover:bg-white/50">
                                    <td className="py-1.5 px-2 text-stone-700">{name}</td>
                                    {filteredYards.map(yard => (
                                      <td key={yard.id} className="py-1.5 px-2 text-center">{yard.tools?.[key] || 0}</td>
                                    ))}
                                    {selectedYard === 'all' && <td className="py-1.5 px-2 text-center font-semibold bg-stone-100">{total}</td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Render custom categories */}
                  {customCategories.map(cat => {
                    const categoryTools = toolsByCategory[cat.key] || [];
                    if (categoryTools.length === 0) return null;
                    
                    return (
                      <div key={cat.key} className="bg-purple-50 border-purple-200 border rounded-lg p-3">
                        <h4 className="font-semibold text-purple-800 mb-2">✨ {cat.name}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-purple-200">
                                <th className="text-left py-2 px-2 font-medium text-stone-600 w-36">Tool</th>
                                {filteredYards.map(yard => (
                                  <th key={yard.id} className="text-center py-2 px-2 font-medium text-stone-600 w-20 truncate" title={yard.name}>{yard.name}</th>
                                ))}
                                {selectedYard === 'all' && <th className="text-center py-2 px-2 font-bold text-stone-800 bg-stone-100 w-16">Total</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {categoryTools.map(({ key, name }) => {
                                const total = filteredYards.reduce((sum, y) => sum + (y.tools?.[key] || 0), 0);
                                return (
                                  <tr key={key} className="border-b border-purple-100/50 hover:bg-white/50">
                                    <td className="py-1.5 px-2 text-purple-700">{name}</td>
                                    {filteredYards.map(yard => (
                                      <td key={yard.id} className="py-1.5 px-2 text-center">{yard.tools?.[key] || 0}</td>
                                    ))}
                                    {selectedYard === 'all' && <td className="py-1.5 px-2 text-center font-semibold bg-purple-100">{total}</td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Custom tools are now integrated into the categorized sections above */}
                </div>
              );
            })()}
            </div>
            
            {/* Custom components are now integrated into the categorized Hive Components section below */}
            
            {/* Hive Components by Category */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 mt-6">
              <h3 className="text-lg font-bold text-blue-800 mb-3">📦 Hive Components</h3>
            {(() => {
              // Define component categories with their colors and icons
              const componentCategories = {
                'lids_covers': { label: 'Lids & Covers', bg: 'bg-sky-50', text: 'text-sky-800', border: 'border-sky-200', icon: '🏠' },
                'bottom_boards': { label: 'Bottom Boards & Stands', bg: 'bg-slate-50', text: 'text-slate-800', border: 'border-slate-200', icon: '⬇️' },
                'feeders': { label: 'Feeders', bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200', icon: '🍯' },
                'queen_management': { label: 'Queen Management', bg: 'bg-pink-50', text: 'text-pink-800', border: 'border-pink-200', icon: '👑' },
                'pest_control': { label: 'Pest Control & Accessories', bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-200', icon: '🐛' },
                'heavy_accessories': { label: 'Heavy & Accessories', bg: 'bg-stone-50', text: 'text-stone-800', border: 'border-stone-200', icon: '⚙️' },
              };
              
              // Build components by category entirely from componentsConfig (admin DB)
              const allComponents = componentsConfig?.items || [];
              const componentsByCategory = {};
              allComponents.forEach(comp => {
                const section = comp.section || 'pest_control';
                if (!componentsByCategory[section]) componentsByCategory[section] = [];
                const formKey = comp.form_key || comp.name?.toLowerCase().replace(/\s+/g, '_') || comp.key || comp.id;
                componentsByCategory[section].push({ key: formKey, name: comp.name });
              });
              
              // Get custom categories
              const customComponentCategories = componentsConfig?.custom_categories || [];
              
              // Get the filtered yards for the table
              const filteredYardNames = Object.keys(filteredEquipmentSummary.by_yard || {});
              
              return (
                <div className="space-y-4">
                  {/* Render each category */}
                  {Object.entries(componentCategories).map(([categoryKey, category]) => {
                    const allCompsInCategory = componentsByCategory[categoryKey] || [];
                    
                    // Only show categories with components
                    if (allCompsInCategory.length === 0) return null;
                    
                    return (
                      <div key={categoryKey} className={`${category.bg} ${category.border} border rounded-lg p-3`}>
                        <h4 className={`font-semibold ${category.text} mb-2`}>{category.icon} {category.label}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={`border-b ${category.border}`}>
                                <th className="text-left py-2 px-2 font-medium text-stone-600 w-36">Component</th>
                                {filteredYardNames.map(yardName => (
                                  <th key={yardName} className="text-center py-2 px-2 font-medium text-stone-600 w-20 truncate" title={yardName}>{yardName}</th>
                                ))}
                                {selectedYard === 'all' && <th className="text-center py-2 px-2 font-bold text-stone-800 bg-stone-100 w-16">Total</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {allCompsInCategory.map(({ key, name }) => {
                                const total = Object.values(filteredEquipmentSummary.by_yard || {}).reduce((sum, d) => sum + (d.components?.[key] || 0), 0);
                                return (
                                  <tr key={key} className="border-b border-stone-100/50 hover:bg-white/50">
                                    <td className="py-1.5 px-2 text-stone-700">{name}</td>
                                    {Object.values(filteredEquipmentSummary.by_yard || {}).map((data, idx) => (
                                      <td key={idx} className="py-1.5 px-2 text-center">{data.components?.[key] || 0}</td>
                                    ))}
                                    {selectedYard === 'all' && <td className="py-1.5 px-2 text-center font-semibold bg-stone-100">{total}</td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Render custom component categories */}
                  {customComponentCategories.map(cat => {
                    const categoryComps = componentsByCategory[cat.key] || [];
                    if (categoryComps.length === 0) return null;
                    
                    return (
                      <div key={cat.key} className="bg-violet-50 border-violet-200 border rounded-lg p-3">
                        <h4 className="font-semibold text-violet-800 mb-2">✨ {cat.name}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-violet-200">
                                <th className="text-left py-2 px-2 font-medium text-stone-600 w-36">Component</th>
                                {filteredYardNames.map(yardName => (
                                  <th key={yardName} className="text-center py-2 px-2 font-medium text-stone-600 w-20 truncate" title={yardName}>{yardName}</th>
                                ))}
                                {selectedYard === 'all' && <th className="text-center py-2 px-2 font-bold text-stone-800 bg-stone-100 w-16">Total</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {categoryComps.map(({ key, name }) => {
                                const total = Object.values(filteredEquipmentSummary.by_yard || {}).reduce((sum, d) => sum + (d.components?.[key] || 0), 0);
                                return (
                                  <tr key={key} className="border-b border-violet-100/50 hover:bg-white/50">
                                    <td className="py-1.5 px-2 text-violet-700">{name}</td>
                                    {Object.values(filteredEquipmentSummary.by_yard || {}).map((data, idx) => (
                                      <td key={idx} className="py-1.5 px-2 text-center">{data.components?.[key] || 0}</td>
                                    ))}
                                    {selectedYard === 'all' && <td className="py-1.5 px-2 text-center font-semibold bg-violet-100">{total}</td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Custom components are now integrated into the categorized sections above */}
                </div>
              );
            })()}
            </div>

            {/* Legacy tools section removed - all tools now shown in categorized Apiary Tools section above */}

            {/* Products & Sales Analytics Section */}
            {(salesSummary?.total_revenue > 0 || products.length > 0) && (
              <>
                <div className="flex items-center justify-between mt-8 mb-3">
                  <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5 text-emerald-600" />
                    Products & Sales
                  </h3>
                </div>

                {/* Revenue Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-700">${(salesSummary?.total_revenue || 0).toFixed(2)}</div>
                    <div className="text-xs text-stone-500">Total Revenue</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{Object.values(salesSummary?.by_product_type || {}).reduce((sum, t) => sum + (t.count || 0), 0)}</div>
                    <div className="text-xs text-stone-500">Total Sales</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-amber-700">{products.length}</div>
                    <div className="text-xs text-stone-500">Products Listed</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-700">{Object.keys(salesSummary?.by_product_type || {}).length}</div>
                    <div className="text-xs text-stone-500">Product Types Sold</div>
                  </div>
                </div>

                {/* Revenue by Product Type */}
                {Object.keys(salesSummary?.by_product_type || {}).length > 0 && (
                  <div className="bg-white border border-stone-200 rounded-lg p-4 mb-4">
                    <h4 className="font-bold text-stone-800 mb-3">Revenue by Product Type</h4>
                    <div className="space-y-2">
                      {Object.entries(salesSummary.by_product_type)
                        .sort((a, b) => b[1].revenue - a[1].revenue)
                        .map(([type, data]) => {
                          const maxRev = Math.max(...Object.values(salesSummary.by_product_type).map(d => d.revenue));
                          const pct = maxRev > 0 ? (data.revenue / maxRev) * 100 : 0;
                          return (
                            <div key={type} className="flex items-center gap-3">
                              <div className="w-24 text-sm font-medium text-stone-700 capitalize">{type.replace(/_/g, ' ')}</div>
                              <div className="flex-1 h-6 bg-stone-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full flex items-center justify-end pr-2 transition-all" style={{ width: `${Math.max(pct, 8)}%` }}>
                                  <span className="text-xs text-white font-semibold">${data.revenue.toFixed(0)}</span>
                                </div>
                              </div>
                              <div className="w-16 text-right text-xs text-stone-500">{data.count} sold</div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Revenue by Month */}
                  {Object.keys(salesSummary?.by_month || {}).length > 0 && (
                    <div className="bg-white border border-stone-200 rounded-lg p-4">
                      <h4 className="font-bold text-stone-800 mb-3">Monthly Revenue</h4>
                      <div className="space-y-2">
                        {Object.entries(salesSummary.by_month)
                          .sort((a, b) => b[0].localeCompare(a[0]))
                          .slice(0, 6)
                          .map(([month, data]) => {
                            const [y, m] = month.split('-');
                            const label = new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
                            return (
                              <div key={month} className="flex items-center justify-between text-sm">
                                <span className="text-stone-600">{label}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-stone-400">{data.count} sales</span>
                                  <span className="font-bold text-emerald-700">${data.revenue.toFixed(2)}</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Revenue by Apiary */}
                  {Object.keys(salesSummary?.by_yard || {}).length > 0 && (
                    <div className="bg-white border border-stone-200 rounded-lg p-4">
                      <h4 className="font-bold text-stone-800 mb-3">Revenue by Apiary</h4>
                      <div className="space-y-2">
                        {Object.entries(salesSummary.by_yard)
                          .sort((a, b) => b[1].revenue - a[1].revenue)
                          .map(([yard, data]) => (
                            <div key={yard} className="flex items-center justify-between text-sm">
                              <span className="text-stone-600">{yard}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-stone-400">{data.count} sales</span>
                                <span className="font-bold text-emerald-700">${data.revenue.toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Inventory */}
                {products.length > 0 && (
                  <div className="bg-white border border-stone-200 rounded-lg p-4 mb-4">
                    <h4 className="font-bold text-stone-800 mb-3">Product Inventory</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-stone-200 text-left text-stone-500">
                            <th className="pb-2 font-medium">Product</th>
                            <th className="pb-2 font-medium">Type</th>
                            <th className="pb-2 font-medium text-right">Last Sold Price</th>
                            <th className="pb-2 font-medium text-right">Stock</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map(p => {
                            // Derive price from recent sales
                            const matchingSale = (salesSummary?.recent_sales || []).find(s => 
                              s.product_name?.includes(p.name) || p.name?.includes(s.product_name)
                            );
                            const unitPrice = matchingSale ? (matchingSale.total_price / (matchingSale.quantity || 1)) : null;
                            return (
                              <tr key={p.id} className="border-b border-stone-50">
                                <td className="py-2 font-medium text-stone-800">{p.name}</td>
                                <td className="py-2 text-stone-500 capitalize">{(p.product_type || p.category || '').replace(/_/g, ' ')}</td>
                                <td className="py-2 text-right text-stone-700">{unitPrice ? `$${unitPrice.toFixed(2)}` : '—'}</td>
                                <td className="py-2 text-right">{p.stock_quantity != null ? <span className={p.stock_quantity <= 0 ? 'text-red-500 font-medium' : 'text-stone-700'}>{p.stock_quantity} {p.unit || ''}</span> : '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recent Sales */}
                {(salesSummary?.recent_sales || []).length > 0 && (
                  <div className="bg-white border border-stone-200 rounded-lg p-4">
                    <h4 className="font-bold text-stone-800 mb-3">Recent Sales</h4>
                    <div className="space-y-2">
                      {salesSummary.recent_sales.slice(0, 8).map(s => (
                        <div key={s.id} className="flex items-center justify-between text-sm border-b border-stone-50 pb-2">
                          <div>
                            <span className="font-medium text-stone-800">{s.product_name}</span>
                            <span className="text-stone-400 ml-2">x{s.quantity}</span>
                            {s.customer_name && <span className="text-stone-400 ml-2">— {s.customer_name}</span>}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-stone-400">{s.date}</span>
                            <span className="font-bold text-emerald-700">${(s.total_price || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Queen Rearing Analytics Section */}
            {queenRearingData && (queenRearingData.stats.totalBatches > 0 || queenRearingData.stats.totalBreeders > 0 || queenRearingData.stats.totalNucs > 0) && (
              <>
                <div className="flex items-center justify-between mt-8 mb-3">
                  <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2">
                    <Crown className="h-5 w-5 text-amber-600" />
                    Queen Rearing
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportQueenRearingCSV}
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportQueenRearingPDF}
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </div>
                
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                      <div className="text-2xl font-bold text-amber-700">{queenRearingData.stats.activeBatches}</div>
                      <div className="text-xs text-amber-600">Active Batches</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-100 text-center">
                      <div className="text-2xl font-bold text-purple-700">{queenRearingData.stats.activeBreeders}</div>
                      <div className="text-xs text-purple-600">Active Breeders</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-blue-100 text-center">
                      <div className="text-2xl font-bold text-blue-700">{queenRearingData.stats.totalNucs}</div>
                      <div className="text-xs text-blue-600">Mating Nucs</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-100 text-center">
                      <div className="text-2xl font-bold text-green-700">{queenRearingData.stats.successRate}%</div>
                      <div className="text-xs text-green-600">Success Rate</div>
                    </div>
                  </div>
                  
                  {/* Cell Statistics */}
                  <div className="bg-white rounded-lg p-3 border border-amber-100 mb-4">
                    <h4 className="text-sm font-semibold text-amber-800 mb-2">Cell Statistics</h4>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div>
                        <div className="text-lg font-bold text-stone-700">{queenRearingData.stats.totalCells}</div>
                        <div className="text-xs text-stone-500">Grafted</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-amber-600">{queenRearingData.stats.cellsEmerged}</div>
                        <div className="text-xs text-stone-500">Emerged</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-green-600">{queenRearingData.stats.cellsLaying}</div>
                        <div className="text-xs text-stone-500">Laying</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-red-600">{queenRearingData.stats.cellsFailed}</div>
                        <div className="text-xs text-stone-500">Failed</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Batches Table */}
                  <ApiaryQueenComparison batches={queenRearingData.allBatches} />
                  {queenRearingData.batches.length > 0 && (
                    <div className="bg-white rounded-lg border border-amber-100 overflow-hidden mb-4">
                      <h4 className="text-sm font-semibold text-amber-800 p-3 bg-amber-100/50">📦 Batches ({queenRearingData.batches.length})</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-amber-50">
                            <tr>
                              <th className="text-left py-2 px-3 text-amber-800">Batch Name</th>
                              <th className="text-left py-2 px-3 text-amber-800">Apiary</th>
                              <th className="text-left py-2 px-3 text-amber-800">Graft Date</th>
                              <th className="text-left py-2 px-3 text-amber-800">Breeder</th>
                              <th className="text-left py-2 px-3 text-amber-800">Cell Starter</th>
                              <th className="text-left py-2 px-3 text-amber-800">Finisher</th>
                              <th className="text-center py-2 px-3 text-amber-800">Cells</th>
                              <th className="text-center py-2 px-3 text-amber-800">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {queenRearingData.batches.map((batch, idx) => (
                              <tr key={batch.id || idx} className={`border-t border-amber-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}`}>
                                <td className="py-2 px-3 font-medium text-stone-700">
                                  {batch.batch_name || `Batch #${batch.batch_number}`}
                                </td>
                                <td className="py-2 px-3 text-teal-700" data-testid={`analytics-qr-batch-apiary-${idx}`}>{batch.yard_name || '-'}</td>
                                <td className="py-2 px-3 text-stone-600">{batch.graft_date || '-'}</td>
                                <td className="py-2 px-3 text-purple-600">{batch.breeder_queen_name || '-'}</td>
                                <td className="py-2 px-3 text-stone-600">{batch.cell_builder_hive_number ? `Hive #${batch.cell_builder_hive_number}` : '-'}</td>
                                <td className="py-2 px-3 text-stone-600">{batch.finisher_hive_number ? `Hive #${batch.finisher_hive_number}` : (batch.cell_builder_hive_number ? 'Same' : '-')}</td>
                                <td className="py-2 px-3 text-center text-stone-700">{batch.cells_grafted || 0}</td>
                                <td className="py-2 px-3 text-center">
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    batch.status === 'complete' ? 'bg-green-100 text-green-700' :
                                    batch.status === 'active' ? 'bg-blue-100 text-blue-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                    {batch.status || 'planning'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Breeders & Nucs Summary */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Breeder Queens */}
                    {queenRearingData.breeders.length > 0 && (
                      <div className="bg-white rounded-lg border border-purple-100 overflow-hidden">
                        <h4 className="text-sm font-semibold text-purple-800 p-3 bg-purple-100/50">👑 Breeder Queens ({queenRearingData.breeders.length})</h4>
                        <div className="p-2 space-y-2 max-h-48 overflow-y-auto">
                          {queenRearingData.breeders.map((b, idx) => (
                            <div key={b.id || idx} className="flex items-center justify-between p-2 bg-purple-50/50 rounded">
                              <div>
                                <span className="font-medium text-stone-700">{b.name || 'Unnamed'}</span>
                                {b.genetics && <span className="text-xs text-purple-600 ml-2">({b.genetics})</span>}
                                {b.yard_name && <span className="block text-xs text-teal-700">📍 {b.yard_name}</span>}
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                b.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'
                              }`}>
                                {b.status || 'unknown'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Mating Nucs */}
                    {queenRearingData.nucs.length > 0 && (
                      <div className="bg-white rounded-lg border border-blue-100 overflow-hidden">
                        <h4 className="text-sm font-semibold text-blue-800 p-3 bg-blue-100/50">🏠 Mating Nucs ({queenRearingData.nucs.length})</h4>
                        <div className="p-2 space-y-2 max-h-48 overflow-y-auto">
                          {queenRearingData.nucs.map((n, idx) => (
                            <div key={n.id || idx} className="flex items-center justify-between p-2 bg-blue-50/50 rounded">
                              <div>
                                <span className="font-medium text-stone-700">Nuc #{n.nuc_number || idx + 1}</span>
                                {n.nuc_type && <span className="text-xs text-blue-600 ml-2">({n.nuc_type})</span>}
                                {n.yard_name && <span className="block text-xs text-teal-700">📍 {n.yard_name}</span>}
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                n.status === 'available' ? 'bg-green-100 text-green-700' :
                                n.status === 'occupied' ? 'bg-amber-100 text-amber-700' :
                                'bg-stone-100 text-stone-600'
                              }`}>
                                {n.status || 'unknown'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-amber-600 mt-3 text-center">
                    💡 Tip: Go to Queen Rearing to manage batches, breeders, and mating nucs
                  </p>
                </div>
              </>
            )}

            {/* Swarm Trap Report Section */}
            {swarmTrapSummary && swarmTrapSummary.total_traps > 0 && (
              <>
                <div className="flex items-center justify-between mt-8 mb-3">
                  <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2">
                    <Target className="h-5 w-5 text-teal-600" />
                    🪤 Swarm Trap Report
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/swarm-traps')}
                    className="text-teal-700 border-teal-300 hover:bg-teal-50"
                  >
                    Manage Traps
                  </Button>
                </div>
                
                <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white rounded-lg p-3 border border-teal-100 text-center">
                      <div className="text-2xl font-bold text-teal-700">{swarmTrapSummary.total_traps}</div>
                      <div className="text-xs text-teal-600">Total Traps</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-100 text-center">
                      <div className="text-2xl font-bold text-green-700">{swarmTrapSummary.active_traps}</div>
                      <div className="text-xs text-green-600">Active</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-amber-100 text-center">
                      <div className="text-2xl font-bold text-amber-700">{swarmTrapSummary.catches_this_season}</div>
                      <div className="text-xs text-amber-600">Catches This Season</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-purple-100 text-center">
                      <div className="text-2xl font-bold text-purple-700">{swarmTrapSummary.success_rate}%</div>
                      <div className="text-xs text-purple-600">Success Rate</div>
                    </div>
                  </div>
                  
                  {/* Catches Timeline Chart */}
                  {swarmTrapSummary.catches_timeline && swarmTrapSummary.catches_timeline.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-teal-100 mb-4">
                      <h4 className="text-sm font-semibold text-teal-800 mb-3">📊 Catches Over Time</h4>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={swarmTrapSummary.catches_timeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="month" 
                              tick={{ fontSize: 11, fill: '#6b7280' }}
                              tickFormatter={(value) => {
                                const [year, month] = value.split('-');
                                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                return monthNames[parseInt(month) - 1] || value;
                              }}
                            />
                            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                            <Tooltip 
                              formatter={(value) => [value, 'Catches']}
                              labelFormatter={(value) => {
                                const [year, month] = value.split('-');
                                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                return `${monthNames[parseInt(month) - 1]} ${year}`;
                              }}
                            />
                            <Bar dataKey="catches" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                  
                  {/* Two Column Layout: Map and Outcomes */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                    {/* Trap Locations Map */}
                    {swarmTrapSummary.trap_locations && swarmTrapSummary.trap_locations.some(t => t.gps_lat && t.gps_lng) && (
                      <div className="bg-white rounded-lg p-3 border border-teal-100">
                        <h4 className="text-sm font-semibold text-teal-800 mb-2">📍 Trap Locations</h4>
                        <div className="h-48 rounded-lg overflow-hidden">
                          <MapContainer
                            center={(() => {
                              const validLocs = swarmTrapSummary.trap_locations.filter(t => t.gps_lat && t.gps_lng);
                              if (validLocs.length > 0) {
                                const avgLat = validLocs.reduce((sum, t) => sum + t.gps_lat, 0) / validLocs.length;
                                const avgLng = validLocs.reduce((sum, t) => sum + t.gps_lng, 0) / validLocs.length;
                                return [avgLat, avgLng];
                              }
                              return [-38.0, 145.0];
                            })()}
                            zoom={10}
                            style={{ height: '100%', width: '100%' }}
                          >
                            <TileLayer
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              attribution='&copy; OpenStreetMap contributors'
                            />
                            {swarmTrapSummary.trap_locations.filter(t => t.gps_lat && t.gps_lng).map((trap, idx) => (
                              <Marker
                                key={trap.id || idx}
                                position={[trap.gps_lat, trap.gps_lng]}
                                icon={L.divIcon({
                                  className: 'custom-marker',
                                  html: `<div style="background: ${trap.status === 'captured' ? '#f59e0b' : trap.status === 'converted' ? '#10b981' : '#14b8a6'}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; font-size: 12px;">🪤</div>`,
                                  iconSize: [24, 24],
                                  iconAnchor: [12, 12]
                                })}
                              >
                                <Popup>
                                  <div className="text-sm">
                                    <strong>{trap.name}</strong>
                                    <div className="text-xs text-stone-500">
                                      {trap.location_type === 'apiary' ? `At: ${trap.yard}` : trap.location || 'Remote location'}
                                    </div>
                                    <div className={`text-xs mt-1 ${trap.status === 'captured' ? 'text-amber-600' : trap.status === 'converted' ? 'text-green-600' : 'text-teal-600'}`}>
                                      {trap.status === 'captured' ? '🐝 Swarm Captured' : trap.status === 'converted' ? '✅ Converted to Hive' : '⏳ Active'}
                                    </div>
                                  </div>
                                </Popup>
                              </Marker>
                            ))}
                          </MapContainer>
                        </div>
                        <div className="flex justify-center gap-4 mt-2 text-xs">
                          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-teal-500"></span> Active</span>
                          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500"></span> Captured</span>
                          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500"></span> Converted</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Outcomes Summary */}
                    <div className="bg-white rounded-lg p-3 border border-teal-100">
                      <h4 className="text-sm font-semibold text-teal-800 mb-2">📈 Catch Outcomes</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                          <span className="text-sm text-green-700">🏠 Converted to Hive</span>
                          <span className="font-bold text-green-700">{swarmTrapSummary.outcomes?.converted_to_hive || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                          <span className="text-sm text-blue-700">📦 Transferred to NUC</span>
                          <span className="font-bold text-blue-700">{swarmTrapSummary.outcomes?.transferred_to_nuc || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                          <span className="text-sm text-red-700">💨 Absconded</span>
                          <span className="font-bold text-red-700">{swarmTrapSummary.outcomes?.absconded || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-stone-50 rounded-lg">
                          <span className="text-sm text-stone-700">📋 Other/In Progress</span>
                          <span className="font-bold text-stone-700">{swarmTrapSummary.outcomes?.other || 0}</span>
                        </div>
                      </div>
                      
                      {/* All Time Stats */}
                      <div className="mt-4 p-3 bg-teal-100/50 rounded-lg">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-teal-700">{swarmTrapSummary.catches_all_time}</div>
                          <div className="text-xs text-teal-600">Total Catches (All Time)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Trap Performance Table */}
                  {swarmTrapSummary.top_traps && swarmTrapSummary.top_traps.length > 0 && (
                    <div className="bg-white rounded-lg border border-teal-100 overflow-hidden">
                      <h4 className="text-sm font-semibold text-teal-800 p-3 bg-teal-100/50">🏆 Top Performing Traps</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-teal-50">
                            <tr>
                              <th className="text-left py-2 px-3 text-teal-800">Rank</th>
                              <th className="text-left py-2 px-3 text-teal-800">Trap Name</th>
                              <th className="text-center py-2 px-3 text-teal-800">Catches</th>
                            </tr>
                          </thead>
                          <tbody>
                            {swarmTrapSummary.top_traps.map((trap, idx) => (
                              <tr key={trap.name || idx} className={`border-t border-teal-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-teal-50/30'}`}>
                                <td className="py-2 px-3 text-stone-500">
                                  {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                                </td>
                                <td className="py-2 px-3 font-medium text-stone-700">{trap.name}</td>
                                <td className="py-2 px-3 text-center">
                                  <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                                    {trap.catches} {trap.catches === 1 ? 'catch' : 'catches'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-teal-600 text-center py-2 mt-2">
                    💡 Tip: Place traps at 2-3m height with lemon grass oil for best results during swarm season (Spring)
                  </p>
                </div>
              </>
            )}

            {/* Cost Summary Section - Last Section */}
            {costSummary && (
              <>
                <div className="flex items-center justify-between mt-8 mb-3">
                  <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    💰 Cost Summary
                  </h3>
                  <div className="flex gap-2">
                    {showCosts && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={exportCostCSV}
                          className="text-green-700 border-green-300 hover:bg-green-50"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={exportCostPDF}
                          className="text-green-700 border-green-300 hover:bg-green-50"
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCosts(!showCosts)}
                      className={showCosts ? "bg-green-100 border-green-300" : ""}
                    >
                      {showCosts ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {showCosts ? 'Hide Costs' : 'Show Costs'}
                    </Button>
                  </div>
                </div>
                
                {showCosts && (
                  <div className="space-y-4">
                    {/* Calculate Harvest Equipment Investment and Revenue */}
                    {(() => {
                      const harvestEquipmentInvestment = harvestConfig?.harvesting_equipment?.reduce((sum, e) => {
                        return sum + ((parseFloat(e.qty) || 0) * (parseFloat(e.price) || 0));
                      }, 0) || 0;
                      const totalWithHarvestEquip = (costSummary.grand_total || 0) + harvestEquipmentInvestment;
                      
                      // Calculate revenue metrics
                      let totalRevenue = 0;
                      let soldRevenue = 0;
                      let potentialRevenue = 0;
                      let soldCount = 0;
                      let inStockCount = 0;
                      
                      (harvests || []).forEach(h => {
                        const revenue = (parseFloat(h.quantity) || 0) * (parseFloat(h.sale_price) || 0);
                        totalRevenue += revenue;
                        if (h.is_sold) {
                          soldRevenue += revenue;
                          soldCount += 1;
                        } else {
                          potentialRevenue += revenue;
                          inStockCount += 1;
                        }
                      });
                      
                      const netProfit = soldRevenue - totalWithHarvestEquip;
                      
                      return (
                        <>
                          {/* Investment Summary Card */}
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200 shadow-sm">
                            <h4 className="text-sm font-semibold text-green-800 mb-4 flex items-center gap-2">
                              📊 Investment Overview
                            </h4>
                            
                            {/* Grand Total */}
                            <div className="flex items-center justify-between mb-4 p-4 bg-white rounded-lg border border-green-200">
                              <div>
                                <span className="text-sm text-green-600 font-medium">Total Investment</span>
                                <p className="text-xs text-stone-500 mt-0.5">Apiaries + Hives + Harvesting Equipment</p>
                              </div>
                              <span className="text-3xl font-bold text-green-700">${totalWithHarvestEquip.toFixed(2)}</span>
                            </div>
                            
                            {/* Investment Breakdown Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {/* Apiary & Hive Costs */}
                              <div className="bg-white/80 rounded-lg p-3 border border-green-100">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-green-600">🏠</span>
                                  <span className="text-xs font-medium text-green-700">Apiary & Hive Costs</span>
                                </div>
                                <div className="text-xl font-bold text-green-700">${(costSummary.grand_total || 0).toFixed(2)}</div>
                              </div>
                              
                              {/* Harvesting Equipment */}
                              <div className="bg-white/80 rounded-lg p-3 border border-orange-100">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-orange-600">🔧</span>
                                  <span className="text-xs font-medium text-orange-700">Harvesting Equipment</span>
                                </div>
                                <div className="text-xl font-bold text-orange-700">${harvestEquipmentInvestment.toFixed(2)}</div>
                                {harvestEquipmentInvestment > 0 && (
                                  <span className="text-xs text-orange-500">
                                    {harvestConfig?.harvesting_equipment?.filter(e => (parseInt(e.qty) || 0) > 0).length || 0} items
                                  </span>
                                )}
                              </div>
                              
                              {/* Net Profit/Loss */}
                              {totalRevenue > 0 && (
                                <div className={`bg-white/80 rounded-lg p-3 border ${netProfit >= 0 ? 'border-emerald-100' : 'border-red-100'}`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}>💰</span>
                                    <span className={`text-xs font-medium ${netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Net Profit</span>
                                  </div>
                                  <div className={`text-xl font-bold ${netProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                                    ${netProfit.toFixed(2)}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Revenue Section - if exists */}
                            {totalRevenue > 0 && (
                              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-semibold text-emerald-800">Revenue Summary</span>
                                  <span className="text-lg font-bold text-emerald-700">${totalRevenue.toFixed(2)}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="bg-white/60 rounded p-2 text-center">
                                    <div className="text-base font-bold text-green-600">${soldRevenue.toFixed(2)}</div>
                                    <div className="text-xs text-green-600">Sold ({soldCount})</div>
                                  </div>
                                  <div className="bg-white/60 rounded p-2 text-center">
                                    <div className="text-base font-bold text-amber-600">${potentialRevenue.toFixed(2)}</div>
                                    <div className="text-xs text-amber-600">In Stock ({inStockCount})</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                    
                    {/* Cost by Apiary Card */}
                    <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                      <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                        🏡 Cost by Apiary
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-green-200 bg-green-50">
                              <th className="text-left py-3 px-3 font-semibold text-green-800">Yard</th>
                              <th className="text-right py-3 px-3 font-semibold text-green-800">Apiary Equipment</th>
                              <th className="text-right py-3 px-3 font-semibold text-green-800">Hive Costs</th>
                              <th className="text-right py-3 px-3 font-semibold text-green-800">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(costSummary.by_yard || {})
                              .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard)
                              .map(([yardName, data]) => (
                              <tr key={yardName} className="border-b border-green-100 hover:bg-green-50/50 transition-colors">
                                <td className="py-3 px-3 font-medium text-stone-700">{yardName}</td>
                                <td className="py-3 px-3 text-right text-stone-600">${(data.yard_equipment_cost || 0).toFixed(2)}</td>
                                <td className="py-3 px-3 text-right text-stone-600">${(data.total_hive_cost || 0).toFixed(2)}</td>
                                <td className="py-3 px-3 text-right font-bold text-green-700">${(data.total_cost || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          {selectedYard === 'all' && (
                            <tfoot>
                              <tr className="bg-green-100">
                                <td className="py-3 px-3 font-bold text-green-800">Total</td>
                                <td className="py-3 px-3 text-right font-bold text-green-800">
                                  ${Object.values(costSummary.by_yard || {}).reduce((sum, d) => sum + (d.yard_equipment_cost || 0), 0).toFixed(2)}
                                </td>
                                <td className="py-3 px-3 text-right font-bold text-green-800">
                                  ${Object.values(costSummary.by_yard || {}).reduce((sum, d) => sum + (d.total_hive_cost || 0), 0).toFixed(2)}
                                </td>
                                <td className="py-3 px-3 text-right font-bold text-green-800 text-lg">
                                  ${(costSummary.grand_total || 0).toFixed(2)}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>

                    {/* Harvesting Equipment Breakdown Card */}
                    {harvestConfig?.harvesting_equipment && harvestConfig.harvesting_equipment.some(e => (parseInt(e.qty) || 0) > 0) && (
                      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200 shadow-sm">
                        {(() => {
                          const equipmentWithQty = harvestConfig.harvesting_equipment.filter(e => (parseInt(e.qty) || 0) > 0);
                          const totalEquipCost = equipmentWithQty.reduce((sum, e) => sum + ((parseInt(e.qty) || 0) * (parseFloat(e.price) || 0)), 0);
                          return (
                            <>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-orange-800 flex items-center gap-2">
                                  🔧 Harvesting Equipment Breakdown
                                  <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full">
                                    {equipmentWithQty.length} items
                                  </span>
                                </h4>
                                <span className="text-lg font-bold text-orange-700">${totalEquipCost.toFixed(2)}</span>
                              </div>
                              <div className="overflow-x-auto bg-white/60 rounded-lg">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-orange-200 bg-orange-100/50">
                                      <th className="text-left py-2 px-3 font-medium text-orange-800">Equipment</th>
                                      <th className="text-center py-2 px-3 font-medium text-orange-800">Qty</th>
                                      <th className="text-right py-2 px-3 font-medium text-orange-800">Unit Price</th>
                                      <th className="text-right py-2 px-3 font-medium text-orange-800">Subtotal</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {equipmentWithQty.map(equip => {
                                      const qty = parseInt(equip.qty) || 0;
                                      const price = parseFloat(equip.price) || 0;
                                      const subtotal = qty * price;
                                      return (
                                        <tr key={equip.id} className="border-b border-orange-100 hover:bg-orange-50/50 transition-colors">
                                          <td className="py-2 px-3 text-stone-700">
                                            {equip.name}
                                            {equip.is_custom && <span className="text-xs text-orange-500 ml-2">(Custom)</span>}
                                          </td>
                                          <td className="py-2 px-3 text-center text-stone-600">{qty}</td>
                                          <td className="py-2 px-3 text-right text-stone-600">${price.toFixed(2)}</td>
                                          <td className="py-2 px-3 text-right font-medium text-orange-700">${subtotal.toFixed(2)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Hive-level Cost Breakdown Card */}
                    {Object.entries(costSummary.by_yard || {})
                      .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard)
                      .some(([_, data]) => data.hive_costs && data.hive_costs.length > 0 && data.hive_costs.some(h => h.total_cost > 0)) && (
                      <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                        <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                          📋 Hive-Level Cost Breakdown
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-green-200 bg-green-50">
                                <th className="text-left py-2 px-3 font-semibold text-green-700">Yard</th>
                                <th className="text-left py-2 px-3 font-semibold text-green-700">Hive</th>
                                <th className="text-right py-2 px-3 font-semibold text-green-700">Bee Purchase</th>
                                <th className="text-right py-2 px-3 font-semibold text-green-700">Equipment</th>
                                <th className="text-right py-2 px-3 font-semibold text-green-700">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(costSummary.by_yard || {})
                                .filter(([yardName]) => selectedYard === 'all' || yardName === selectedYard)
                                .flatMap(([yardName, data]) => 
                                  (data.hive_costs || [])
                                    .filter(h => h.total_cost > 0)
                                    .map(hive => (
                                      <tr key={hive.hive_id} className="border-b border-green-100 hover:bg-green-50/50 transition-colors">
                                        <td className="py-2 px-3 text-stone-600">{yardName}</td>
                                        <td className="py-2 px-3 text-stone-700">
                                          <span className="font-medium">Hive {hive.hive_number}</span>
                                          {hive.nickname && <span className="text-stone-500 ml-1">({hive.nickname})</span>}
                                        </td>
                                        <td className="py-2 px-3 text-right text-stone-600">${(hive.origin_price || 0).toFixed(2)}</td>
                                        <td className="py-2 px-3 text-right text-stone-600">${(hive.equipment_cost || 0).toFixed(2)}</td>
                                        <td className="py-2 px-3 text-right font-semibold text-green-700">${(hive.total_cost || 0).toFixed(2)}</td>
                                      </tr>
                                    ))
                                )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-green-600 text-center py-2">
                      💡 Tip: Add pricing in Edit Hive or Edit Yard dialogs to track your investment
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, onClick, clickable }) => (
  <div 
    className={`bg-white border border-stone-200 rounded-2xl p-6 shadow-sm ${clickable ? 'cursor-pointer hover:shadow-md hover:border-stone-300 transition-all' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm text-stone-600 font-medium">{title}</div>
        <div className={`text-4xl font-extrabold text-${color}-600 mt-2`}>{value}</div>
        {clickable && <div className="text-xs text-stone-400 mt-1">Click to view</div>}
      </div>
      <div className={`bg-${color}-50 rounded-full p-4`}>
        {icon}
      </div>
    </div>
  </div>
);

export default Analytics;
